// Importar funciones helper para cálculos de estadías
import {
  calcularTiemposMotorEstadia,
  calcularResumenTotalEstadias,
  validarEstadiaParaCalculo
} from './estadiasResumen.js';

// ✅ CONSTANTES COMO EN C#
const Constantes = {
  GEOCERCA: "Geocerca",
  CLIENTE: "Cliente",
  REFERENCIA: "Referencia",
  INDETERMINADO: "Indeterminado"
};


/**
 * ✅ FUNCIÓN GENÉRICA PARA FORMATEAR TIEMPO
 * @param {number} segundos - Tiempo en segundos
 * @returns {string} Tiempo formateado HH:MM:SS
 */
export const formatearTiempo = (segundos) => {
  if (!segundos || isNaN(segundos) || segundos < 0) return "00:00:00";

  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segs = Math.floor(segundos % 60);

  return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
};

/**
 * ✅ FUNCIÓN HELPER PARA DETECTAR REPORTES DE ADMINISTRADOR
 * @param {Object} filtros - Filtros del reporte
 * @returns {boolean} True si es un reporte de administrador
 */
export const esReporteAdministrador = (filtros) => {
  return filtros && 
         filtros.empresas_seleccionadas && 
         Array.isArray(filtros.empresas_seleccionadas) && 
         filtros.empresas_seleccionadas.length > 0;
};

/**
 * ✅ FUNCIÓN HELPER PARA OBTENER EMPRESAS DE UN REPORTE
 * @param {Object} filtros - Filtros del reporte
 * @returns {Array|string} Array de empresas si es multi-empresa, string si es individual
 */
export const obtenerEmpresasReporte = (filtros) => {
  if (esReporteAdministrador(filtros)) {
    return filtros.empresas_seleccionadas;
  }
  return filtros.empresaUid || null;
};

/**
 * ✅ FUNCIÓN GENÉRICA PARA OBTENER TIEMPO FORMATEADO DESDE RESUMEN
 * @param {Object} resumenUnidad - Objeto resumen de unidad
 * @param {string} tipo - Tipo de tiempo ('encendido', 'apagado', 'movimiento', 'detenido')
 * @returns {string} Tiempo formateado con "hrs"
 */
export const obtenerTiempoMotor = (resumenUnidad, tipo) => {
  if (!resumenUnidad) return "00:00:00 hr(s)";

  let segundos = 0;
  switch (tipo) {
    case 'encendido':
      segundos = resumenUnidad.tiempo_actividad || 0;
      break;
    case 'apagado':
      segundos = resumenUnidad.tiempo_apagado || 0;
      break;
    case 'movimiento':
      segundos = resumenUnidad.tiempo_movimiento || 0;
      break;
    case 'detenido':
      segundos = resumenUnidad.tiempo_detenido || 0;
      break;
    default:
      segundos = 0;
  }

  return formatearTiempo(segundos) + " hr(s)";
};

/**
 * ✅ FUNCIÓN GENÉRICA PARA NORMALIZAR FILTROS ENTRE REPORTES
 * @param {Object} filtros - Filtros de cualquier reporte
 * @returns {Object} Filtros normalizados
 */
export const normalizarFiltros = (filtros) => {
  if (!filtros) return {};

  // ✅ DETECTAR SI ES UN REPORTE ESPECIAL PARA ADMINISTRADORES
  const esReporteAdministrador = filtros.empresas_seleccionadas && 
                                 Array.isArray(filtros.empresas_seleccionadas) && 
                                 filtros.empresas_seleccionadas.length > 0;

  // ✅ MANEJAR CAMPOS DE EMPRESA DE FORMA INTELIGENTE
  let camposEmpresa = {};
  
  if (esReporteAdministrador) {
    // Para reportes de administrador, usar las empresas seleccionadas
    camposEmpresa = {
      empresas_seleccionadas: filtros.empresas_seleccionadas,
      // También mantener empresaUid para compatibilidad con APIs que lo requieran
      empresaUid: filtros.empresaUid,
      uid_usuario: filtros.uid_usuario,
      perfil: filtros.perfil,
      // Flag para identificar reportes multi-empresa
      es_reporte_multi_empresa: true
    };
  } else {
    // Para reportes normales, usar el empresaUid estándar
    camposEmpresa = {
      empresaUid: filtros.empresaUid,
      uid_usuario: filtros.uid_usuario,
      perfil: filtros.perfil,
      es_reporte_multi_empresa: false
    };
  }

  return {
    fecha_inicio: filtros.fecha_inicio,
    fecha_fin: filtros.fecha_fin,

    // ✅ NORMALIZAR UIDS DE VEHÍCULOS
    uids_vehiculo: filtros.uids_vehiculo || filtros.uids_vehiculos || [],
    //uids_vehiculos: filtros.uids_vehiculos || filtros.uids_vehiculo || [],

    // ✅ CAMPOS DE EMPRESA INTELIGENTES
    ...camposEmpresa,

    // ✅ OTROS CAMPOS COMUNES
    uids_geocercas: filtros.uids_geocercas || [],
    uids_clientes: filtros.uids_clientes || [],
    uids_referencias: filtros.uids_referencias || [],
    uids_choferes: filtros.uids_choferes || [],

    // ✅ CAMPOS ESPECÍFICOS DE ESTADÍAS
    tipo_vista: filtros.tipo_vista || 0,
    tipo_reporte: filtros.tipo_reporte || 0,
    tiempo_estancia: filtros.tiempo_estancia || 0,
    distancia_estancia: filtros.distancia_estancia || 0,
    encendido_o_apagado: filtros.encendido_o_apagado || "Todo",
  vehiculos_detalle: filtros.vehiculos_detalle || [],

    // ✅ CAMPOS ESPECÍFICOS DE KILOMETRAJE
    distancia_1: filtros.distancia_1 || 0.0,
    distancia_2: filtros.distancia_2 || 0.0,

    // ✅ CAMPOS ADICIONALES
    tipo: filtros.tipo || "Todo",
    tipos: filtros.tipos,
    pagina: filtros.pagina || 0,
    page: filtros.page,
    limit: filtros.limit,
    count: filtros.count,
    order: filtros.order,
  
    recalculo_geocercas: filtros.recalculo_geocercas || false,
    temperatura: filtros.temperatura
  };
};


/**
 * ✅ NUEVA FUNCIÓN: Filtrar recorridos desde primer encendido hasta último apagado
 * @param {Array} recorridos - Lista completa de recorridos
 * @param {string} fechaInicio - Fecha de inicio del filtro
 * @param {string} fechaFin - Fecha de fin del filtro
 * @returns {Array} Recorridos filtrados entre primer encendido y último apagado
 */
export const filtrarPorEventosMotor = (recorridos, fechaInicio, fechaFin) => {
  try {
    if (!recorridos || !Array.isArray(recorridos) || recorridos.length === 0) {
      // console.log('[filtrarPorEventosMotor] No hay recorridos para filtrar');
      return [];
    }

    // console.log(`[filtrarPorEventosMotor] Procesando ${recorridos.length} recorridos`);
    // console.log(`[filtrarPorEventosMotor] Rango: ${fechaInicio} → ${fechaFin}`);

    // ✅ ORDENAR POR FECHA PARA GARANTIZAR SECUENCIA CORRECTA
    const recorridosOrdenados = [...recorridos].sort((a, b) => {
      const fechaA = new Date(a.fecha);
      const fechaB = new Date(b.fecha);
      return fechaA.getTime() - fechaB.getTime();
    });

    // ✅ BUSCAR PRIMER ENCENDIDO (tipos 6 y 4)
    let indicePrimerEncendido = -1;
    let fechaPrimerEncendido = null;

    for (let i = 0; i < recorridosOrdenados.length; i++) {
      const registro = recorridosOrdenados[i];

      // ✅ ENCENDIDO: tipos 6 y 4
      if (registro.tipo === 6 || registro.tipo === 4) {
        indicePrimerEncendido = i;
        fechaPrimerEncendido = registro.fecha;

        // console.log(`[filtrarPorEventosMotor] 🟢 PRIMER ENCENDIDO encontrado:`);
        // console.log(`  - Índice: ${i}`);
        // console.log(`  - Fecha: ${fechaPrimerEncendido}`);
        // console.log(`  - Tipo: ${registro.tipo}`);
        // console.log(`  - Evento: ${registro.nombreEvento}`);
        break;
      }
    }

    // ✅ BUSCAR ÚLTIMO APAGADO (tipos 3 y 5) - DESDE EL FINAL
    let indiceUltimoApagado = -1;
    let fechaUltimoApagado = null;

    for (let i = recorridosOrdenados.length - 1; i >= 0; i--) {
      const registro = recorridosOrdenados[i];

      // ✅ APAGADO: tipos 3 y 5
      if (registro.tipo === 3 || registro.tipo === 5) {
        indiceUltimoApagado = i;
        fechaUltimoApagado = registro.fecha;

        // console.log(`[filtrarPorEventosMotor] 🔴 ÚLTIMO APAGADO encontrado:`);
        // console.log(`  - Índice: ${i}`);
        // console.log(`  - Fecha: ${fechaUltimoApagado}`);
        // console.log(`  - Tipo: ${registro.tipo}`);
        // console.log(`  - Evento: ${registro.nombreEvento}`);
        break;
      }
    }

    // ✅ VALIDACIONES
    if (indicePrimerEncendido === -1 && indiceUltimoApagado === -1) {
      //console.warn('[filtrarPorEventosMotor] ⚠️ No se encontraron eventos de motor, devolviendo todos los recorridos');
      return recorridosOrdenados;
    }

    if (indicePrimerEncendido === -1) {
      //console.warn('[filtrarPorEventosMotor] ⚠️ No se encontró primer encendido, usando desde el inicio');
      indicePrimerEncendido = 0;
      fechaPrimerEncendido = recorridosOrdenados[0].fecha;
    }

    if (indiceUltimoApagado === -1) {
      //console.warn('[filtrarPorEventosMotor] ⚠️ No se encontró último apagado, usando hasta el final');
      indiceUltimoApagado = recorridosOrdenados.length - 1;
      fechaUltimoApagado = recorridosOrdenados[recorridosOrdenados.length - 1].fecha;
    }

    // ✅ VALIDAR ORDEN LÓGICO
    if (indicePrimerEncendido > indiceUltimoApagado) {
      //console.warn('[filtrarPorEventosMotor] ⚠️ Primer encendido está después del último apagado, devolviendo todos los recorridos');
      return recorridosOrdenados;
    }

    // ✅ FILTRAR RECORRIDOS ENTRE LOS ÍNDICES ENCONTRADOS (INCLUSIVO)
    const recorridosFiltrados = recorridosOrdenados.slice(
      indicePrimerEncendido,
      indiceUltimoApagado + 1 // +1 para incluir el último apagado
    );

    // console.log(`[filtrarPorEventosMotor] ✅ RESULTADO:`);
    // console.log(`  - Recorridos originales: ${recorridos.length}`);
    // console.log(`  - Recorridos filtrados: ${recorridosFiltrados.length}`);
    // console.log(`  - Primer encendido: ${fechaPrimerEncendido} (índice ${indicePrimerEncendido})`);
    // console.log(`  - Último apagado: ${fechaUltimoApagado} (índice ${indiceUltimoApagado})`);
    // console.log(`  - Duración filtrada: ${((new Date(fechaUltimoApagado) - new Date(fechaPrimerEncendido)) / (1000 * 60 * 60)).toFixed(2)} horas`);

    // ✅ MOSTRAR ALGUNOS REGISTROS DE MUESTRA
    if (recorridosFiltrados.length > 0) {
      // console.log(`[filtrarPorEventosMotor] 📋 MUESTRA DE REGISTROS FILTRADOS:`);
      // console.log(`  - Primer registro: ${recorridosFiltrados[0].fecha} (tipo: ${recorridosFiltrados[0].tipo})`);

      if (recorridosFiltrados.length > 1) {
        const ultimoIndice = recorridosFiltrados.length - 1;
        // console.log(`  - Último registro: ${recorridosFiltrados[ultimoIndice].fecha} (tipo: ${recorridosFiltrados[ultimoIndice].tipo})`);
      }

      if (recorridosFiltrados.length > 2) {
        const medioIndice = Math.floor(recorridosFiltrados.length / 2);
        // console.log(`  - Registro medio: ${recorridosFiltrados[medioIndice].fecha} (tipo: ${recorridosFiltrados[medioIndice].tipo})`);
      }
    }

    return recorridosFiltrados;

  } catch (error) {
    console.error('[filtrarPorEventosMotor] ❌ Error:', error);
    return recorridos; // Devolver datos originales en caso de error
  }
};

/**
 * 🔍 DEBUG: Análisis detallado de eventos de motor
 * @param {Array} recorridos - Lista completa de recorridos
 * @param {string} fechaInicio - Fecha de inicio del filtro
 * @param {string} fechaFin - Fecha de fin del filtro
 * @returns {Object} Resultado con análisis detallado
 */
export const debugFiltrarEventosMotor = (recorridos, fechaInicio, fechaFin) => {
  // console.log('🔍 === ANÁLISIS DETALLADO DE EVENTOS DE MOTOR ===');

  if (!recorridos || !Array.isArray(recorridos) || recorridos.length === 0) {
    //console.log('❌ No hay recorridos para analizar');
    return { recorridosFiltrados: [], analisis: null };
  }

  const recorridosOrdenados = [...recorridos].sort((a, b) => {
    const fechaA = new Date(a.fecha);
    const fechaB = new Date(b.fecha);
    return fechaA.getTime() - fechaB.getTime();
  });

  // console.log(`📊 DATOS INICIALES:`);
  // console.log(`  - Total recorridos: ${recorridos.length}`);
  // console.log(`  - Rango solicitado: ${fechaInicio} → ${fechaFin}`);
  // console.log(`  - Primer registro: ${recorridosOrdenados[0]?.fecha}`);
  // console.log(`  - Último registro: ${recorridosOrdenados[recorridosOrdenados.length - 1]?.fecha}`);

  // 🔍 TABLA DE TODOS LOS EVENTOS DE MOTOR
  // console.log('\n📋 TABLA DE EVENTOS DE MOTOR:');
  // console.table(
  //   recorridosOrdenados.map((registro, index) => ({
  //     '#': index,
  //     'Fecha': registro.fecha,
  //     'Tipo': registro.tipo,
  //     'TipoMotor': registro.tipoMotor,
  //     'Evento': registro.nombreEvento || 'N/A',
  //     'Es_Encendido': (registro.tipo === 6 || registro.tipo === 4) ? '🟢 SÍ' : '',
  //     'Es_Apagado': (registro.tipo === 3 || registro.tipo === 5) ? '🔴 SÍ' : '',
  //     'Vehículo': registro.vehiculos?.alias || registro.vehiculoUid
  //   }))
  // );

  // 🔍 BUSCAR EVENTOS ESPECÍFICOS
  const eventosEncendido = [];
  const eventosApagado = [];

  recorridosOrdenados.forEach((registro, index) => {
    if (registro.tipo === 6 || registro.tipo === 4) {
      eventosEncendido.push({
        indice: index,
        fecha: registro.fecha,
        tipo: registro.tipo,
        evento: registro.nombreEvento,
        vehiculo: registro.vehiculos?.alias || registro.vehiculoUid
      });
    }

    if (registro.tipo === 3 || registro.tipo === 5) {
      eventosApagado.push({
        indice: index,
        fecha: registro.fecha,
        tipo: registro.tipo,
        evento: registro.nombreEvento,
        vehiculo: registro.vehiculos?.alias || registro.vehiculoUid
      });
    }
  });

  // console.log('\n🟢 EVENTOS DE ENCENDIDO ENCONTRADOS:');
  if (eventosEncendido.length > 0) {
    // console.table(eventosEncendido);
  } else {
    //console.log('❌ No se encontraron eventos de encendido (tipos 6 y 4)');
  }

  // console.log('\n🔴 EVENTOS DE APAGADO ENCONTRADOS:');
  if (eventosApagado.length > 0) {
    // console.table(eventosApagado);
  } else {
    //console.log('❌ No se encontraron eventos de apagado (tipos 3 y 5)');
  }

  // 🎯 ENCONTRAR PRIMER ENCENDIDO Y ÚLTIMO APAGADO
  const primerEncendido = eventosEncendido.length > 0 ? eventosEncendido[0] : null;
  const ultimoApagado = eventosApagado.length > 0 ? eventosApagado[eventosApagado.length - 1] : null;

  // console.log('\n🎯 EVENTOS SELECCIONADOS:');
  // console.log('🟢 PRIMER ENCENDIDO:', primerEncendido ? {
  //   indice: primerEncendido.indice,
  //   fecha: primerEncendido.fecha,
  //   tipo: primerEncendido.tipo,
  //   evento: primerEncendido.evento,
  //   vehiculo: primerEncendido.vehiculo
  // } : '❌ No encontrado');

  // console.log('🔴 ÚLTIMO APAGADO:', ultimoApagado ? {
  //   indice: ultimoApagado.indice,
  //   fecha: ultimoApagado.fecha,
  //   tipo: ultimoApagado.tipo,
  //   evento: ultimoApagado.evento,
  //   vehiculo: ultimoApagado.vehiculo
  // } : '❌ No encontrado');

  // 🔍 ANÁLISIS DE RANGO FILTRADO
  let indicePrimerEncendido = primerEncendido ? primerEncendido.indice : 0;
  let indiceUltimoApagado = ultimoApagado ? ultimoApagado.indice : recorridosOrdenados.length - 1;

  if (indicePrimerEncendido > indiceUltimoApagado) {
    //console.warn('\n⚠️ PROBLEMA: Primer encendido está después del último apagado');
    return { recorridosFiltrados: recorridosOrdenados, analisis: { error: 'Orden lógico incorrecto' } };
  }

  const recorridosFiltrados = recorridosOrdenados.slice(
    indicePrimerEncendido,
    indiceUltimoApagado + 1
  );

  // 📊 ANÁLISIS FINAL
  const fechaInicioFiltro = new Date(recorridosFiltrados[0]?.fecha);
  const fechaFinFiltro = new Date(recorridosFiltrados[recorridosFiltrados.length - 1]?.fecha);
  const duracionHoras = (fechaFinFiltro - fechaInicioFiltro) / (1000 * 60 * 60);

  // console.log('\n📊 RESULTADO DEL FILTRADO:');
  // console.log(`  - Registros originales: ${recorridos.length}`);
  // console.log(`  - Registros filtrados: ${recorridosFiltrados.length}`);
  // console.log(`  - Reducción: ${((recorridos.length - recorridosFiltrados.length) / recorridos.length * 100).toFixed(1)}%`);
  // console.log(`  - Rango filtrado: ${recorridosFiltrados[0]?.fecha} → ${recorridosFiltrados[recorridosFiltrados.length - 1]?.fecha}`);
  // console.log(`  - Duración: ${duracionHoras.toFixed(2)} horas`);

  // 🔍 MUESTRA DE REGISTROS ELIMINADOS
  const registrosEliminadosInicio = recorridosOrdenados.slice(0, indicePrimerEncendido);
  const registrosEliminadosFin = recorridosOrdenados.slice(indiceUltimoApagado + 1);

  if (registrosEliminadosInicio.length > 0) {
    // console.log('\n❌ REGISTROS ELIMINADOS AL INICIO:');
    // console.table(
    //   registrosEliminadosInicio.slice(0, 5).map((r, i) => ({
    //     '#': i,
    //     'Fecha': r.fecha,
    //     'Tipo': r.tipo,
    //     'Evento': r.nombreEvento,
    //     'Razón': 'Antes del primer encendido'
    //   }))
    // );
    if (registrosEliminadosInicio.length > 5) {
      // console.log(`... y ${registrosEliminadosInicio.length - 5} registros más`);
    }
  }

  if (registrosEliminadosFin.length > 0) {
    // console.log('\n❌ REGISTROS ELIMINADOS AL FINAL:');
    // console.table(
    //   registrosEliminadosFin.slice(0, 5).map((r, i) => ({
    //     '#': i,
    //     'Fecha': r.fecha,
    //     'Tipo': r.tipo,
    //     'Evento': r.nombreEvento,
    //     'Razón': 'Después del último apagado'
    //   }))
    // );
    if (registrosEliminadosFin.length > 5) {
      // console.log(`... y ${registrosEliminadosFin.length - 5} registros más`);
    }
  }

  // 🎯 VALIDACIÓN DE COHERENCIA
  // console.log('\n🔍 VALIDACIÓN DE COHERENCIA:');

  const tiposEnFiltrados = [...new Set(recorridosFiltrados.map(r => r.tipo))];
  // console.log(`  - Tipos de evento en filtrados: [${tiposEnFiltrados.join(', ')}]`);

  // ✅ Registrar logs importantes
  // console.log(`🔍 [FILTRO_MOTOR] Total eventos procesados: ${recorridosFiltrados.length}`);

  // 📈 DISTRIBUCIÓN POR TIPO DE MOTOR
  const distribucionTipoMotor = recorridosFiltrados.reduce((acc, r) => {
    const tipo = r.tipoMotor === 0 ? 'Apagado' : r.tipoMotor === 1 ? 'Encendido' : 'Indefinido';
    acc[tipo] = (acc[tipo] || 0) + 1;
    return acc;
  }, {});

  // console.log('\n📈 DISTRIBUCIÓN POR TIPO DE MOTOR EN FILTRADOS:');
  // console.table(distribucionTipoMotor);

  return {
    recorridosFiltrados,
    analisis: {
      totalOriginal: recorridos.length,
      totalFiltrado: recorridosFiltrados.length,
      reduccionPorcentaje: ((recorridos.length - recorridosFiltrados.length) / recorridos.length * 100).toFixed(1),
      duracionHoras: duracionHoras.toFixed(2),
      primerEncendido,
      ultimoApagado,
      eventosEncendido,
      eventosApagado,
      distribucionTipoMotor,
      registrosEliminadosInicio: registrosEliminadosInicio.length,
      registrosEliminadosFin: registrosEliminadosFin.length
    }
  };
};


/**
 * ✅ FUNCIÓN GENÉRICA PARA CALCULAR RESUMEN DE UNIDAD
 * @param {Array} recorridos - Lista de puntos de recorrido
 * @param {string} fechaInicio - Fecha de inicio del periodo
 * @param {string} fechaFin - Fecha de fin del periodo
 * @returns {Object} Resumen con tiempos calculados
 */
export const calcularResumenUnidad = (recorridos, fechaInicio, fechaFin) => {
  // console.log("🔍 === ANÁLISIS DETALLADO DE CÁLCULO DE RESUMEN ===");
  // console.log(`[calcularResumenUnidad] Iniciando con: ${recorridos?.length} registros`);
  // console.log(`[calcularResumenUnidad] Rango solicitado: ${fechaInicio} → ${fechaFin}`);

  try {
    const resumen = {
      tiempo_actividad: 0,
      tiempo_movimiento: 0,
      tiempo_apagado: 0,
      tiempo_detenido: 0,
      distancia_total: 0,
      velocidad_maxima: 0,
      horas_actividad: "0.00",
      horas_movimiento: "0.00",
      horas_apagado: "0.00",
      horas_detenido: "0.00"
    };

    // ✅ CONTADORES PARA ANÁLISIS DETALLADO
    const contadores = {
      eventosApagado: 0,
      eventosEncendido: 0,
      eventosMovimiento: 0,
      eventosDetenido: 0,
      eventosNoClasificados: 0,
      intervalosDescartados: 0
    };

    const puntosReferencia = {
      primerPunto: null,
      ultimoPunto: null,
      eventosApagado: [],
      eventosEncendido: [],
      eventosMovimiento: [],
      eventosDetenido: [],
      eventosNoClasificados: []
    };

    if (!recorridos || !Array.isArray(recorridos) || recorridos.length === 0) {
      //console.warn("❌ No hay recorridos para procesar");
      return resumen;
    }

    // ✅ ORDENAR POR FECHA
    const recorridosOrdenados = [...recorridos].sort((a, b) => {
      const fechaA = new Date(a.fecha);
      const fechaB = new Date(b.fecha);
      return fechaA.getTime() - fechaB.getTime();
    });

    // ✅ GUARDAR PUNTOS DE REFERENCIA INICIALES
    puntosReferencia.primerPunto = {
      fecha: recorridosOrdenados[0].fecha,
      tipo: recorridosOrdenados[0].tipo,
      tipoMotor: recorridosOrdenados[0].tipoMotor,
      evento: recorridosOrdenados[0].nombreEvento
    };

    puntosReferencia.ultimoPunto = {
      fecha: recorridosOrdenados[recorridosOrdenados.length - 1].fecha,
      tipo: recorridosOrdenados[recorridosOrdenados.length - 1].tipo,
      tipoMotor: recorridosOrdenados[recorridosOrdenados.length - 1].tipoMotor,
      evento: recorridosOrdenados[recorridosOrdenados.length - 1].nombreEvento
    };

    // console.log("📊 PUNTOS DE REFERENCIA INICIALES:");
    // console.log(`  🚀 PRIMER PUNTO: ${puntosReferencia.primerPunto.fecha} (Tipo: ${puntosReferencia.primerPunto.tipo}, TipoMotor: ${puntosReferencia.primerPunto.tipoMotor})`);
    // console.log(`  🏁 ÚLTIMO PUNTO: ${puntosReferencia.ultimoPunto.fecha} (Tipo: ${puntosReferencia.ultimoPunto.tipo}, TipoMotor: ${puntosReferencia.ultimoPunto.tipoMotor})`);

    const duracionTotal = (new Date(puntosReferencia.ultimoPunto.fecha) - new Date(puntosReferencia.primerPunto.fecha)) / (1000 * 60 * 60);
    // console.log(`  ⏱️ DURACIÓN TOTAL DEL DATASET: ${duracionTotal.toFixed(2)} horas`);

    // ✅ CÁLCULO DE DISTANCIA Y VELOCIDAD MÁXIMA
    resumen.distancia_total = recorridosOrdenados.reduce((total, punto) => {
      const distancia = parseFloat(punto.distancia || 0);
      return total + (isNaN(distancia) ? 0 : distancia);
    }, 0);

    resumen.velocidad_maxima = recorridosOrdenados.reduce((max, punto) => {
      const velocidad = parseFloat(punto.velocidad || 0);
      return Math.max(max, isNaN(velocidad) ? 0 : velocidad);
    }, 0);

    // console.log(`📏 MÉTRICAS BÁSICAS:`);
    // console.log(`  - Distancia total: ${resumen.distancia_total.toFixed(2)} km`);
    // console.log(`  - Velocidad máxima: ${resumen.velocidad_maxima.toFixed(2)} km/h`);

    // console.log("\n🔄 === PROCESAMIENTO DE INTERVALOS ===");

    // ✅ PROCESAR CADA INTERVALO CON LOGS DETALLADOS
    for (let i = 0; i < recorridosOrdenados.length - 1; i++) {
      const puntoActual = recorridosOrdenados[i];
      const puntoSiguiente = recorridosOrdenados[i + 1];

      const fechaActual = new Date(puntoActual.fecha);
      const fechaSiguiente = new Date(puntoSiguiente.fecha);
      const duracionSegundos = (fechaSiguiente - fechaActual) / 1000;

      // ✅ VERIFICAR INTERVALOS RAZONABLES
      if (duracionSegundos <= 0 || duracionSegundos > 7200) {
        contadores.intervalosDescartados++;
        //console.warn(`⚠️ [INTERVALO ${i}] DESCARTADO - Duración inválida: ${duracionSegundos}s`);
        continue;
      }

      // console.log(`\n📍 [INTERVALO ${i}]:`);
      // console.log(`  📅 Desde: ${fechaActual.toLocaleString()}`);
      // console.log(`  📅 Hasta: ${fechaSiguiente.toLocaleString()}`);
      // console.log(`  ⏱️ Duración: ${formatearTiempo(duracionSegundos)} (${duracionSegundos}s)`);
      // console.log(`  🔧 Punto actual - Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Evento: "${puntoActual.nombreEvento}"`);

      // ✅ CLASIFICAR SEGÚN ESPECIFICACIONES EXACTAS
      let clasificacion = 'NO_CLASIFICADO';

      // 🔴 EVENTOS DE APAGADO: tipos 1,2,3,5 + tipoMotor=0
      if ((puntoActual.tipo === 1 || puntoActual.tipo === 2 || puntoActual.tipo === 3 || puntoActual.tipo === 5) &&
        puntoActual.tipoMotor === 0) {

        resumen.tiempo_apagado += duracionSegundos;
        contadores.eventosApagado++;
        clasificacion = 'APAGADO';

        puntosReferencia.eventosApagado.push({
          indice: i,
          fechaInicio: puntoActual.fecha,
          fechaFin: puntoSiguiente.fecha,
          duracion: duracionSegundos,
          tipo: puntoActual.tipo,
          tipoMotor: puntoActual.tipoMotor,
          evento: puntoActual.nombreEvento
        });

        // console.log(`  🔴 ✅ [APAGADO] ${formatearTiempo(duracionSegundos)} agregado al tiempo de apagado`);
        // console.log(`  🔴 MOTOR APAGADO DETECTADO: ${formatearTiempo(duracionSegundos)} - Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Evento: "${puntoActual.nombreEvento}", Fecha: ${puntoActual.fecha}`);
        // console.log(`  📊 Total apagado acumulado: ${formatearTiempo(resumen.tiempo_apagado)}`);
      }

      // 🛑 EVENTOS DE DETENIDO: tipos 11,12,17,18,30 + tipoMotor=1
      else if ((puntoActual.tipo === 11 || puntoActual.tipo === 12 || puntoActual.tipo === 17 ||
        puntoActual.tipo === 18 || puntoActual.tipo === 30) &&
        puntoActual.tipoMotor === 1) {

        resumen.tiempo_detenido += duracionSegundos;
        contadores.eventosDetenido++;
        clasificacion = 'DETENIDO';

        puntosReferencia.eventosDetenido.push({
          indice: i,
          fechaInicio: puntoActual.fecha,
          fechaFin: puntoSiguiente.fecha,
          duracion: duracionSegundos,
          tipo: puntoActual.tipo,
          tipoMotor: puntoActual.tipoMotor,
          evento: puntoActual.nombreEvento
        });

        // console.log(`  🛑 ✅ [DETENIDO] ${formatearTiempo(duracionSegundos)} agregado al tiempo detenido`);
        // console.log(`  📊 Total detenido acumulado: ${formatearTiempo(resumen.tiempo_detenido)}`);
      }

      // 🚗 EVENTOS DE MOVIMIENTO: cualquier otro evento + tipoMotor=1
      else if (puntoActual.tipoMotor === 1) {
        resumen.tiempo_movimiento += duracionSegundos;
        contadores.eventosMovimiento++;
        clasificacion = 'MOVIMIENTO';

        puntosReferencia.eventosMovimiento.push({
          indice: i,
          fechaInicio: puntoActual.fecha,
          fechaFin: puntoSiguiente.fecha,
          duracion: duracionSegundos,
          tipo: puntoActual.tipo,
          tipoMotor: puntoActual.tipoMotor,
          evento: puntoActual.nombreEvento
        });

        // console.log(`  🚗 ✅ [MOVIMIENTO] ${formatearTiempo(duracionSegundos)} agregado al tiempo de movimiento`);
        // console.log(`  📊 Total movimiento acumulado: ${formatearTiempo(resumen.tiempo_movimiento)}`);
      }

      // ⚠️ CASOS NO CLASIFICADOS
      else {
        contadores.eventosNoClasificados++;
        clasificacion = 'NO_CLASIFICADO';

        puntosReferencia.eventosNoClasificados.push({
          indice: i,
          fechaInicio: puntoActual.fecha,
          fechaFin: puntoSiguiente.fecha,
          duracion: duracionSegundos,
          tipo: puntoActual.tipo,
          tipoMotor: puntoActual.tipoMotor,
          evento: puntoActual.nombreEvento
        });

        //console.warn(`  ⚠️ [NO CLASIFICADO] Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Duración: ${formatearTiempo(duracionSegundos)}`);
      }

      // console.log(`  🏷️ CLASIFICACIÓN: ${clasificacion}`);
    }

    // ✅ CONVERTIR A HORAS
    resumen.horas_actividad = (resumen.tiempo_actividad / 3600).toFixed(2);
    resumen.horas_movimiento = (resumen.tiempo_movimiento / 3600).toFixed(2);
    resumen.horas_apagado = (resumen.tiempo_apagado / 3600).toFixed(2);
    resumen.horas_detenido = (resumen.tiempo_detenido / 3600).toFixed(2);

    // ✅ VALIDACIÓN FINAL
    Object.keys(resumen).forEach(key => {
      if (isNaN(resumen[key]) || resumen[key] === null || resumen[key] === undefined) {
        if (key.includes('horas')) {
          resumen[key] = "0.00";
        } else {
          resumen[key] = 0;
        }
      }
    });

    // ✅ ANÁLISIS FINAL DETALLADO
    // console.log("\n📊 === RESUMEN FINAL DETALLADO ===");

    // console.log("🔢 CONTADORES DE EVENTOS:");
    // console.log(`  🔴 Eventos APAGADO: ${contadores.eventosApagado}`);
    // console.log(`  🚗 Eventos MOVIMIENTO: ${contadores.eventosMovimiento}`);
    // console.log(`  🛑 Eventos DETENIDO: ${contadores.eventosDetenido}`);
    // console.log(`  ⚠️ Eventos NO CLASIFICADOS: ${contadores.eventosNoClasificados}`);
    // console.log(`  ❌ Intervalos descartados: ${contadores.intervalosDescartados}`);

    // console.log("\n⏱️ TIEMPOS CALCULADOS:");
    // console.log(`  🔴 Tiempo APAGADO: ${formatearTiempo(resumen.tiempo_apagado)} (${resumen.horas_apagado} hrs)`);
    // console.log(`  🚗 Tiempo MOVIMIENTO: ${formatearTiempo(resumen.tiempo_movimiento)} (${resumen.horas_movimiento} hrs)`);
    // console.log(`  🛑 Tiempo DETENIDO: ${formatearTiempo(resumen.tiempo_detenido)} (${resumen.horas_detenido} hrs)`);
    // console.log(`  🟢 Tiempo ENCENDIDO: ${formatearTiempo(resumen.tiempo_actividad)} (${resumen.horas_actividad} hrs)`);

    // ✅ VERIFICACIÓN DE COHERENCIA
    // console.log("\n🔍 VERIFICACIÓN DE COHERENCIA:");
    // console.log(`  📏 Tiempo total calculado: ${formatearTiempo(tiempoTotalCalculado)} (${(tiempoTotalCalculado / 3600).toFixed(2)} hrs)`);
    // console.log(`  📏 Duración total dataset: ${formatearTiempo(duracionTotalDataset)} (${(duracionTotalDataset / 3600).toFixed(2)} hrs)`);
    // console.log(`  📊 Diferencia: ${formatearTiempo(Math.abs(duracionTotalDataset - tiempoTotalCalculado))}`);
    // console.log(`  💯 Cobertura: ${((tiempoTotalCalculado / duracionTotalDataset) * 100).toFixed(1)}%`);

    // ✅ MOSTRAR MUESTRAS DE EVENTOS
    // console.log("\n📋 MUESTRAS DE EVENTOS (Primeros 3 de cada tipo):");

    if (puntosReferencia.eventosApagado.length > 0) {
      // console.log("🔴 EVENTOS APAGADO:");
      puntosReferencia.eventosApagado.slice(0, 3).forEach((evento, i) => {
        // console.log(`  ${i + 1}. ${evento.fechaInicio} → ${evento.fechaFin} (${formatearTiempo(evento.duracion)}) - Tipo: ${evento.tipo}`);
      });
      if (puntosReferencia.eventosApagado.length > 3) {
        // console.log(`  ... y ${puntosReferencia.eventosApagado.length - 3} eventos más`);
      }
    }

    if (puntosReferencia.eventosMovimiento.length > 0) {
      // console.log("🚗 EVENTOS MOVIMIENTO:");
      puntosReferencia.eventosMovimiento.slice(0, 3).forEach((evento, i) => {
        // console.log(`  ${i + 1}. ${evento.fechaInicio} → ${evento.fechaFin} (${formatearTiempo(evento.duracion)}) - Tipo: ${evento.tipo}`);
      });
      if (puntosReferencia.eventosMovimiento.length > 3) {
        // console.log(`  ... y ${puntosReferencia.eventosMovimiento.length - 3} eventos más`);
      }
    }

    if (puntosReferencia.eventosDetenido.length > 0) {
      // console.log("🛑 EVENTOS DETENIDO:");
      puntosReferencia.eventosDetenido.slice(0, 3).forEach((evento, i) => {
        // console.log(`  ${i + 1}. ${evento.fechaInicio} → ${evento.fechaFin} (${formatearTiempo(evento.duracion)}) - Tipo: ${evento.tipo}`);
      });
      if (puntosReferencia.eventosDetenido.length > 3) {
        // console.log(`  ... y ${puntosReferencia.eventosDetenido.length - 3} eventos más`);
      }
    }

    if (puntosReferencia.eventosNoClasificados.length > 0) {
      // console.log("⚠️ EVENTOS NO CLASIFICADOS:");
      puntosReferencia.eventosNoClasificados.slice(0, 3).forEach((evento, i) => {
        // console.log(`  ${i + 1}. ${evento.fechaInicio} → ${evento.fechaFin} (${formatearTiempo(evento.duracion)}) - Tipo: ${evento.tipo}, TipoMotor: ${evento.tipoMotor}`);
      });
      if (puntosReferencia.eventosNoClasificados.length > 3) {
        // console.log(`  ... y ${puntosReferencia.eventosNoClasificados.length - 3} eventos más`);
      }
    }

    // console.log("✅ === FIN DEL ANÁLISIS ===\n");

    return resumen;

  } catch (error) {
    console.error("❌ [calcularResumenUnidad] Error:", error);
    return {
      tiempo_actividad: 0,
      tiempo_movimiento: 0,
      tiempo_apagado: 0,
      tiempo_detenido: 0,
      distancia_total: 0,
      velocidad_maxima: 0,
      horas_actividad: "0.00",
      horas_movimiento: "0.00",
      horas_apagado: "0.00",
      horas_detenido: "0.00"
    };
  }
};


export const calcularResumenUnidad_respaldo_10062025 = (recorridos, fechaInicio, fechaFin) => {
  //console.log("[calcularResumenUnidad] Iniciando con:", recorridos?.length, "registros");
  //console.table(recorridos);
  try {
    const resumen = {
      tiempo_actividad: 0,
      tiempo_movimiento: 0,
      tiempo_apagado: 0,
      tiempo_detenido: 0,
      distancia_total: 0,
      velocidad_maxima: 0,
      horas_actividad: "0.00",
      horas_movimiento: "0.00",
      horas_apagado: "0.00",
      horas_detenido: "0.00"
    };

    if (!recorridos || !Array.isArray(recorridos) || recorridos.length === 0) {
      return resumen;
    }

    // ✅ ORDENAR POR FECHA
    const recorridosOrdenados = [...recorridos].sort((a, b) => {
      const fechaA = new Date(a.fecha);
      const fechaB = new Date(b.fecha);
      return fechaA.getTime() - fechaB.getTime();
    });

    // ✅ CÁLCULO DE DISTANCIA Y VELOCIDAD MÁXIMA
    resumen.distancia_total = recorridosOrdenados.reduce((total, punto) => {
      const distancia = parseFloat(punto.distancia || 0);
      return total + (isNaN(distancia) ? 0 : distancia);
    }, 0);

    resumen.velocidad_maxima = recorridosOrdenados.reduce((max, punto) => {
      const velocidad = parseFloat(punto.velocidad || 0);
      return Math.max(max, isNaN(velocidad) ? 0 : velocidad);
    }, 0);

    // ✅ LÓGICA SIMPLE: CALCULAR TIEMPO ENTRE EVENTOS CONSECUTIVOS
    // for (let i = 0; i < recorridosOrdenados.length - 1; i++) {
    //   const puntoActual = recorridosOrdenados[i];
    //   const puntoSiguiente = recorridosOrdenados[i + 1];

    //   const fechaActual = new Date(puntoActual.fecha);
    //   const fechaSiguiente = new Date(puntoSiguiente.fecha);
    //   const duracionSegundos = (fechaSiguiente - fechaActual) / 1000;

    //   // Solo intervalos razonables (máximo 2 horas)
    //   if (duracionSegundos <= 0 || duracionSegundos > 7200) {
    //     continue;
    //   }

    //   // ✅ CLASIFICAR SEGÚN EL TIPO DE EVENTO ACTUAL

    //   // 🛑 EVENTOS DE DETENIDO (tipos 11, 12, 17, 18, 30)
    //   if (puntoActual.tipo === 11 || puntoActual.tipo === 12 || 
    //       puntoActual.tipo === 17 || puntoActual.tipo === 18 || 
    //       puntoActual.tipo === 30) {
    //     resumen.tiempo_detenido += duracionSegundos;
    //     console.log(`[DETENIDO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo})`);
    //   }

    //   else if (puntoActual.tipo === 2 || puntoActual.tipo === 3 || puntoActual.tipo === 5 || 
    //        puntoActual.tipoMotor === 0 ||
    //        (puntoActual.nombreEvento && puntoActual.nombreEvento.toLowerCase().includes('apagado'))) {
    //     resumen.tiempo_apagado += duracionSegundos;
    //     console.log(`✅ [APAGADO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo}, evento: ${puntoActual.nombreEvento})`);
    //   }

    //   // 🚗 EVENTOS DE MOVIMIENTO (tipo 1)
    //    else if ((puntoActual.tipo === 1 && puntoActual.nombreEvento === 'Movimiento') ||
    //        puntoActual.tipo === 13 || puntoActual.tipo === 14 ||
    //        (puntoActual.nombreEvento && puntoActual.nombreEvento.toLowerCase().includes('movimiento'))) {
    //     resumen.tiempo_movimiento += duracionSegundos;
    //     console.log(`✅ [MOVIMIENTO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo})`);
    //   }
    //   // else if (puntoActual.tipo === 1 || puntoActual.nombreEvento === 'Movimiento') {
    //   //   resumen.tiempo_movimiento += duracionSegundos;
    //   //   console.log(`[MOVIMIENTO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()}`);
    //   // }

    //   // 🔴 EVENTOS DE APAGADO (tipos 3, 5, o tipoMotor=0)
    //   // else if (puntoActual.tipo === 3 || puntoActual.tipo === 5 || puntoActual.tipo === 2 || 
    //   //          (puntoActual.nombreEvento === 'Apagado' && puntoActual.tipoMotor === 0)) {
    //   //   resumen.tiempo_apagado += duracionSegundos;
    //   //   console.log(`[APAGADO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo})`);
    //   // }

    //   // 🟢 EVENTOS DE ENCENDIDO (tipos 4, 6, o tipoMotor=1)
    //   else if (puntoActual.tipo === 4 || puntoActual.tipo === 6) {
    //     resumen.tiempo_actividad += duracionSegundos;
    //     console.log(`[ENCENDIDO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo})`);
    //   }

    //   // 🔧 FALLBACK: Usar tipoMotor si no hay evento específico
    //   else if (puntoActual.tipoMotor === 0) {
    //     resumen.tiempo_apagado += duracionSegundos;
    //     console.log(`[APAGADO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipoMotor=0)`);
    //   }
    //   else if (puntoActual.tipoMotor === 1) {
    //     // Distinguir entre encendido parado vs movimiento
    //     const velocidad = parseFloat(puntoActual.velocidad || 0);
    //     if (velocidad > 5) {
    //       resumen.tiempo_movimiento += duracionSegundos;
    //       console.log(`[MOVIMIENTO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (velocidad: ${velocidad})`);
    //     } else {
    //       resumen.tiempo_actividad += duracionSegundos;
    //       console.log(`[ENCENDIDO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (velocidad baja: ${velocidad})`);
    //     }
    //   }
    // }
    for (let i = 0; i < recorridosOrdenados.length - 1; i++) {
      const puntoActual = recorridosOrdenados[i];
      const puntoSiguiente = recorridosOrdenados[i + 1];

      const fechaActual = new Date(puntoActual.fecha);
      const fechaSiguiente = new Date(puntoSiguiente.fecha);
      const duracionSegundos = (fechaSiguiente - fechaActual) / 1000;

      // Solo intervalos razonables (máximo 2 horas)
      if (duracionSegundos <= 0 || duracionSegundos > 7200) {
        continue;
      }

      //console.log(`[EVENTO ${i}] Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Evento: ${puntoActual.nombreEvento}, Duración: ${formatearTiempo(duracionSegundos)}`);

      // ✅ CLASIFICAR SEGÚN ESPECIFICACIONES EXACTAS

      // 🔴 EVENTOS DE APAGADO: tipos 1,2,3,5 + tipoMotor=0
      if ((puntoActual.tipo === 1 || puntoActual.tipo === 2 || puntoActual.tipo === 3 || puntoActual.tipo === 5) &&
        puntoActual.tipoMotor === 0) {
        resumen.tiempo_apagado += duracionSegundos;
        //console.log(`  🔴 MOTOR APAGADO (calcularTiempoMotor): ${formatearTiempo(duracionSegundos)} - Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Evento: "${puntoActual.nombreEvento}", Fecha: ${puntoActual.fecha}`);
      }

      // 🛑 EVENTOS DE DETENIDO: tipos 11,12,17,18,30 + tipoMotor=1
      else if ((puntoActual.tipo === 11 || puntoActual.tipo === 12 || puntoActual.tipo === 17 ||
        puntoActual.tipo === 18 || puntoActual.tipo === 30) &&
        puntoActual.tipoMotor === 1) {
        resumen.tiempo_detenido += duracionSegundos;
        // console.log(`✅ [DETENIDO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo}, tipoMotor: ${puntoActual.tipoMotor})`);
      }

      // 🚗 EVENTOS DE MOVIMIENTO: cualquier otro evento + tipoMotor=1
      else if (puntoActual.tipoMotor === 1) {
        resumen.tiempo_movimiento += duracionSegundos;
        // console.log(`✅ [MOVIMIENTO] ${formatearTiempo(duracionSegundos)} desde ${fechaActual.toLocaleTimeString()} (tipo ${puntoActual.tipo}, tipoMotor: ${puntoActual.tipoMotor})`);
      }

      // ⚠️ CASOS NO CLASIFICADOS (para debugging)
      else {
        //console.warn(`⚠️ [NO CLASIFICADO] Tipo: ${puntoActual.tipo}, TipoMotor: ${puntoActual.tipoMotor}, Duración: ${formatearTiempo(duracionSegundos)}`);
        // Opcional: agregar a tiempo_actividad o ignorar
        // resumen.tiempo_actividad += duracionSegundos;
      }
    }
    // ✅ CONVERTIR A HORAS
    resumen.horas_actividad = (resumen.tiempo_actividad / 3600).toFixed(2);
    resumen.horas_movimiento = (resumen.tiempo_movimiento / 3600).toFixed(2);
    resumen.horas_apagado = (resumen.tiempo_apagado / 3600).toFixed(2);
    resumen.horas_detenido = (resumen.tiempo_detenido / 3600).toFixed(2);

    // ✅ VALIDACIÓN FINAL
    Object.keys(resumen).forEach(key => {
      if (isNaN(resumen[key]) || resumen[key] === null || resumen[key] === undefined) {
        if (key.includes('horas')) {
          resumen[key] = "0.00";
        } else {
          resumen[key] = 0;
        }
      }
    });

    //console.log("[calcularResumenUnidad] 📊 RESUMEN FINAL:");
    //console.log(`- Tiempo apagado: ${formatearTiempo(resumen.tiempo_apagado)} (${resumen.horas_apagado} hrs)`);
    //console.log(`- Tiempo encendido: ${formatearTiempo(resumen.tiempo_actividad)} (${resumen.horas_actividad} hrs)`);
    //console.log(`- Tiempo movimiento: ${formatearTiempo(resumen.tiempo_movimiento)} (${resumen.horas_movimiento} hrs)`);
    //console.log(`- Tiempo detenido: ${formatearTiempo(resumen.tiempo_detenido)} (${resumen.horas_detenido} hrs) ⭐`);
    //console.log(`- Distancia total: ${resumen.distancia_total.toFixed(2)} km`);

    return resumen;

  } catch (error) {
    //console.error("[calcularResumenUnidad] Error:", error);
    return {
      tiempo_actividad: 0,
      tiempo_movimiento: 0,
      tiempo_apagado: 0,
      tiempo_detenido: 0,
      distancia_total: 0,
      velocidad_maxima: 0,
      horas_actividad: "0.00",
      horas_movimiento: "0.00",
      horas_apagado: "0.00",
      horas_detenido: "0.00"
    };
  }
};

// export const calcularResumenUnidad = (recorridos, fechaInicio, fechaFin) => {
//   console.log("[calcularResumenUnidad] Iniciando con:", recorridos?.length, "registros");

//   try {
//     // ✅ RESULTADOS INICIALES SEGUROS
//     const resumen = {
//       tiempo_actividad: 0,
//       tiempo_movimiento: 0,
//       tiempo_apagado: 0,
//       tiempo_detenido: 0,
//       distancia_total: 0,
//       velocidad_maxima: 0,
//       horas_actividad: "0.00",
//       horas_movimiento: "0.00", 
//       horas_apagado: "0.00",
//       horas_detenido: "0.00"
//     };

//     // ✅ VALIDACIÓN GENÉRICA DE ENTRADA
//     if (!recorridos || !Array.isArray(recorridos) || recorridos.length === 0) {
//       console.log("[calcularResumenUnidad] No hay recorridos para procesar");
//       return resumen;
//     }

//     // ✅ ORDENAR POR FECHA DE MANERA SEGURA
//     const recorridosOrdenados = [...recorridos].sort((a, b) => {
//       const fechaA = new Date(a.fecha || a.fechaCorta || 0);
//       const fechaB = new Date(b.fecha || b.fechaCorta || 0);
//       return fechaA.getTime() - fechaB.getTime();
//     });

//     console.log("[calcularResumenUnidad] Procesando", recorridosOrdenados.length, "registros ordenados");

//     // ✅ CÁLCULO SEGURO DE DISTANCIA Y VELOCIDAD MÁXIMA
//     resumen.distancia_total = recorridosOrdenados.reduce((total, punto) => {
//       const distancia = parseFloat(punto.distancia || punto.promedio_distancia || 0);
//       return total + (isNaN(distancia) ? 0 : distancia);
//     }, 0);

//     resumen.velocidad_maxima = recorridosOrdenados.reduce((max, punto) => {
//       const velocidad = parseFloat(punto.velocidad || punto.velocidad_maxima || 0);
//       return Math.max(max, isNaN(velocidad) ? 0 : velocidad);
//     }, 0);

//     // ✅ CÁLCULO DE TIEMPOS BASADO EN ESTADOS DEL MOTOR
//     let tiempoTotalApagado = 0;
//     let tiempoTotalEncendido = 0;
//     let estadoAnterior = null;
//     let fechaAnterior = null;

//     // Variables para tracking de movimiento/detenido
//     let tiempoMovimiento = 0;
//     let tiempoDetenido = 0;
//     let ultimoEstadoMovimiento = null;
//     let fechaUltimoMovimiento = null;

//     for (let i = 0; i < recorridosOrdenados.length; i++) {
//       const punto = recorridosOrdenados[i];

//       // ✅ OBTENER FECHA DE MANERA GENÉRICA
//       const fechaStr = punto.fecha || punto.fechaCorta || punto.fecha_inicio;
//       const fechaActual = new Date(fechaStr);

//       // Validar fecha
//       if (isNaN(fechaActual.getTime())) {
//         console.warn("[calcularResumenUnidad] Fecha inválida:", fechaStr);
//         continue;
//       }

//       // ✅ DETERMINAR ESTADO DEL MOTOR DE MANERA GENÉRICA
//       let estadoMotor = null;
//       if (punto.tipoMotor === 0 || punto.tipo_motor === 0) {
//         estadoMotor = 'apagado';
//       } else if (punto.tipoMotor === 1 || punto.tipo_motor === 1) {
//         estadoMotor = 'encendido';
//       } else if (punto.tipo === 3 || punto.tipo === 5) {
//         estadoMotor = 'apagado';
//       } else if (punto.tipo === 4 || punto.tipo === 6) {
//         estadoMotor = 'encendido';
//       }

//       // ✅ DETERMINAR ESTADO DE MOVIMIENTO DE MANERA GENÉRICA
//       let estadoMovimiento = null;
//       const velocidad = parseFloat(punto.velocidad || 0);

//       if (punto.nombreEvento?.toLowerCase().includes('movimiento') || velocidad > 5) {
//         estadoMovimiento = 'movimiento';
//       } else if (punto.tipo === 11 || punto.tipo === 12 || punto.tipo === 17 || 
//                  punto.tipo === 18 || punto.tipo === 30 || velocidad <= 5) {
//         estadoMovimiento = 'detenido';
//       }

//       // Calcular tiempo transcurrido desde el punto anterior
//       if (fechaAnterior && estadoAnterior) {
//         const tiempoTranscurrido = (fechaActual - fechaAnterior) / 1000; // segundos

//         // Solo considerar intervalos razonables (menos de 4 horas)
//         if (tiempoTranscurrido > 0 && tiempoTranscurrido < 14400) {
//           if (estadoAnterior === 'apagado') {
//             tiempoTotalApagado += tiempoTranscurrido;
//           } else if (estadoAnterior === 'encendido') {
//             tiempoTotalEncendido += tiempoTranscurrido;
//           }
//         }
//       }

//       // Calcular tiempo de movimiento/detenido
//       if (fechaUltimoMovimiento && ultimoEstadoMovimiento) {
//         const tiempoTranscurrido = (fechaActual - fechaUltimoMovimiento) / 1000;

//         if (tiempoTranscurrido > 0 && tiempoTranscurrido < 14400) {
//           if (ultimoEstadoMovimiento === 'movimiento') {
//             tiempoMovimiento += tiempoTranscurrido;
//           } else if (ultimoEstadoMovimiento === 'detenido') {
//             tiempoDetenido += tiempoTranscurrido;
//           }
//         }
//       }

//       // Actualizar estados anteriores
//       if (estadoMotor) {
//         estadoAnterior = estadoMotor;
//         fechaAnterior = fechaActual;
//       }

//       if (estadoMovimiento) {
//         ultimoEstadoMovimiento = estadoMovimiento;
//         fechaUltimoMovimiento = fechaActual;
//       }
//     }

//     // ✅ ASIGNAR RESULTADOS DE MANERA SEGURA
//     resumen.tiempo_apagado = tiempoTotalApagado;
//     resumen.tiempo_movimiento = tiempoMovimiento;
//     resumen.tiempo_detenido = tiempoDetenido;
//     resumen.tiempo_actividad = tiempoTotalEncendido;

//     // ✅ CONVERTIR A HORAS CON VALIDACIÓN
//     resumen.horas_actividad = (resumen.tiempo_actividad / 3600).toFixed(2);
//     resumen.horas_movimiento = (resumen.tiempo_movimiento / 3600).toFixed(2);
//     resumen.horas_apagado = (resumen.tiempo_apagado / 3600).toFixed(2);
//     resumen.horas_detenido = (resumen.tiempo_detenido / 3600).toFixed(2);

//     // ✅ VALIDAR QUE NO HAYA NaN
//     Object.keys(resumen).forEach(key => {
//       if (isNaN(resumen[key]) || resumen[key] === null || resumen[key] === undefined) {
//         if (key.includes('horas')) {
//           resumen[key] = "0.00";
//         } else {
//           resumen[key] = 0;
//         }
//       }
//     });

//     console.log("[calcularResumenUnidad] Resultado:", resumen);
//     return resumen;

//   } catch (error) {
//     console.error("[calcularResumenUnidad] Error:", error);

//     // ✅ RETORNO SEGURO EN CASO DE ERROR
//     return {
//       tiempo_actividad: 0,
//       tiempo_movimiento: 0,
//       tiempo_apagado: 0,
//       tiempo_detenido: 0,
//       distancia_total: 0,
//       velocidad_maxima: 0,
//       horas_actividad: "0.00",
//       horas_movimiento: "0.00",
//       horas_apagado: "0.00", 
//       horas_detenido: "0.00"
//     };
//   }
// };


/**
 * Calcula la distancia entre dos puntos geográficos en metros
 * @param {number} lon1 - Longitud del punto 1
 * @param {number} lat1 - Latitud del punto 1
 * @param {number} lon2 - Longitud del punto 2
 * @param {number} lat2 - Latitud del punto 2
 * @returns {number} Distancia en metros
 */
export const calcularDistancia = (lon1, lat1, lon2, lat2) => {
  const R = 6371; // Radio de la Tierra en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c * 1000; // Convertir a metros
  return distance;
};

/**
 * Concatena información de geocercas, clientes y referencias - CORREGIDO SEGÚN C#
 * @param {Object} punto - Punto del recorrido
 * @returns {Object} Objeto con strings concatenados
 */
export const grcConcat = (punto) => {
  try {
    let geocercas = '';
    let clientes = '';
    let referencias = '';

    // ✅ GEOCERCAS - MANTENER IGUAL (ya funciona)
    if (punto.geocercas && punto.geocercas.geocercasasignadas) {
      const listaGeo = punto.geocercas.geocercasasignadas
        .filter(i => i.geocercaUid !== "0")
        .map(i => i.nombreGeocerca || i.NombreGeocerca);
      geocercas = listaGeo.join(',').trim();
    }

    // ✅ CLIENTES - USAR NOMBRES (NO Num_Tienda)
    if (punto.clientes && punto.clientes.length > 0) {
      const listaCli = punto.clientes
        .filter(i => (i.Num_Tienda || 0) !== 0)
        .map(i => i.Nombreclientes || i.nombreclientes || i.nombreClientes);
      clientes = listaCli.join(',').trim();
    }

    // ✅ REFERENCIAS - USAR NOMBRES (CORREGIDO)
    if (punto.referencias && punto.referencias.length > 0) {
      const listaRef = punto.referencias
        .filter(i => (i.referenciaUid || i.ReferenciaUid) !== "0")
        .map(i => i.nombreReferencia || i.NombreReferencia);
      referencias = listaRef.join(',').trim();
    }

    //console.log(`[grcConcat] Resultado para punto:`, {
    //  fecha: punto.fecha,
    //  geocercas,
    //  clientes,
    //  referencias
    //});

    return {
      geocercas,
      referencias,
      clientes
    };
  } catch (error) {
    console.error('Error en grcConcat:', error);
    return { geocercas: '', referencias: '', clientes: '' };
  }
};


/**
 * Calcula estadías por detenido (vehículo parado)
 * @param {Array} puntos - Array de puntos del recorrido
 * @param {number} tiempo - Tiempo mínimo para considerar una estadía (minutos)
 * @param {number} distancia - Distancia máxima para considerar una estadía (metros)
 * @returns {Array} Puntos de inicio y fin de cada estadía
 */
export const obtenerEstadiasDetenido = (puntos, tiempo = 5, distancia = 50) => {
  try {
    const respuesta = [];
    let puntoInicio = null;
    let ultimaAlerta = "";

    puntos = puntos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    puntos.forEach((punto, contador) => {
      let metros = 0;

      // Detectar puntos de detenido (tipos 11, 12, 17, 18, 30)
      if (punto.tipo === 11 || punto.tipo === 12 || punto.tipo === 17 ||
        punto.tipo === 18 || punto.tipo === 30) {

        // Guardar información de alerta detenido
        if (punto.tipo === 17 || punto.tipo === 18) {
          const latitud = punto.location?.coordinates?.[1] || punto.latitud || 0;
          const longitud = punto.location?.coordinates?.[0] || punto.longitud || 0;
          ultimaAlerta = `${punto.fecha};${latitud};${longitud}`;
        }

        // Guardar primer punto detenido
        if (!puntoInicio) {
          puntoInicio = punto;
          return; // Continuar con el siguiente punto
        }
      }
      else {
        // Si tenemos un inicio y este punto no es detenido, puede ser el fin de una estadía
        if (puntoInicio) {
          // Calcular distancia entre inicio y este punto
          const latitudIni = puntoInicio.location?.coordinates?.[1] || puntoInicio.latitud || 0;
          const longitudIni = puntoInicio.location?.coordinates?.[0] || puntoInicio.longitud || 0;
          const latitudPunto = punto.location?.coordinates?.[1] || punto.latitud || 0;
          const longitudPunto = punto.location?.coordinates?.[0] || punto.longitud || 0;

          metros = calcularDistancia(longitudIni, latitudIni, longitudPunto, latitudPunto);

          // Verificar si cumple la restricción de distancia
          if (distancia > metros) {
            return; // No cumple, seguimos buscando
          } else {
            // Si hay distancia mínima, usar el punto anterior
            if (distancia > 0 && contador > 0) {
              punto = puntos[contador - 1];
            }
          }

          // Verificar el tiempo transcurrido
          const diff = (new Date(punto.fecha) - new Date(puntoInicio.fecha)) / (1000 * 60); // Minutos
          if (diff < tiempo || diff === 0) {
            puntoInicio = null;
            return;
          }

          // Marcar como estadía detenido
          puntoInicio.tipoReporteEstadia = 0;
          punto.tipoReporteEstadia = 0;

          // Guardar referencia a la última alerta
          puntoInicio.ultimaAlertaDetenido = ultimaAlerta;

          // Agregar a la respuesta
          respuesta.push({ ...puntoInicio });
          respuesta.push({ ...punto });
          puntoInicio = null;
        }
      }
    });

    return respuesta;
  } catch (error) {
    console.error('Error en obtenerEstadiasDetenido:', error);
    return [];
  }
};

/**
 * Calcula estadías por encendido/apagado del motor
 * @param {Array} puntos - Array de puntos del recorrido
 * @param {number} tiempo - Tiempo mínimo para considerar una estadía (minutos)
 * @param {number} distancia - Distancia máxima para considerar una estadía (metros)
 * @returns {Array} Puntos de inicio y fin de cada estadía
 */
export const obtenerEstadiasEncendidoApagado = (puntos, tiempo = 5, distancia = 50) => {
  try {
    const respuesta = [];
    let puntoInicio = null;

    puntos = puntos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    for (let x = 0; x < puntos.length; x++) {
      const punto = puntos[x];
      let metros = 0;

      // Detectar punto de apagado
      if (punto.tipo === 5 || punto.tipo === 3) {
        if (!puntoInicio) {
          puntoInicio = punto;
          continue;
        }
      }
      else if ((punto.tipo !== 5 && punto.tipo !== 3) && punto.tipoMotor !== 0) {
        // Si tenemos un inicio y este punto no es apagado, puede ser el fin de una estadía
        if (puntoInicio) {
          const latitudIni = puntoInicio.location?.coordinates?.[1] || puntoInicio.latitud || 0;
          const longitudIni = puntoInicio.location?.coordinates?.[0] || puntoInicio.longitud || 0;
          const latitudPunto = punto.location?.coordinates?.[1] || punto.latitud || 0;
          const longitudPunto = punto.location?.coordinates?.[0] || punto.longitud || 0;

          metros = calcularDistancia(longitudIni, latitudIni, longitudPunto, longitudPunto);

          // Verificar si cumple la restricción de distancia
          if (distancia > metros) {
            puntoInicio = null;
            continue;
          }

          // Verificar el tiempo transcurrido
          const diff = (new Date(punto.fecha) - new Date(puntoInicio.fecha)) / (1000 * 60); // Minutos
          if (diff < tiempo || diff === 0) {
            puntoInicio = null;
            continue;
          }

          // Marcar como estadía encendido/apagado
          puntoInicio.tipoReporteEstadia = 1;
          punto.tipoReporteEstadia = 1;

          // Agregar a la respuesta
          respuesta.push({ ...puntoInicio });
          respuesta.push({ ...punto });
          puntoInicio = null;
        }
        else if ((punto.tipo === 6 || punto.tipo === 4) && punto.tipoMotor === 1 && !puntoInicio) {
          // Buscar el punto de apagado previo
          for (let y = x - 1; y >= 0; y--) {
            const puntoAnterior = puntos[y];
            if ((puntoAnterior.tipo === 1 || puntoAnterior.tipo === 2) && puntoAnterior.tipoMotor === 0) {
              puntoInicio = puntoAnterior;
              break;
            } else if (puntoAnterior.tipo === 6) {
              break;
            }
          }

          if (puntoInicio) {
            const latitudIni = puntoInicio.location?.coordinates?.[1] || puntoInicio.latitud || 0;
            const longitudIni = puntoInicio.location?.coordinates?.[0] || puntoInicio.longitud || 0;
            const latitudPunto = punto.location?.coordinates?.[1] || punto.latitud || 0;
            const longitudPunto = punto.location?.coordinates?.[0] || punto.longitud || 0;

            metros = calcularDistancia(longitudIni, latitudIni, longitudPunto, longitudPunto);

            if (distancia > metros) {
              puntoInicio = null;
              continue;
            }

            const diff = (new Date(punto.fecha) - new Date(puntoInicio.fecha)) / (1000 * 60); // Minutos
            if (diff < tiempo || diff === 0) {
              puntoInicio = null;
              continue;
            }

            puntoInicio.tipoReporteEstadia = 1;
            punto.tipoReporteEstadia = 1;

            respuesta.push({ ...puntoInicio });
            respuesta.push({ ...punto });
            puntoInicio = null;
          }
        }
      }
    }

    return respuesta;
  } catch (error) {
    console.error('Error en obtenerEstadiasEncendidoApagado:', error);
    return [];
  }
};

/**
 * Calcula estadías de punto a punto (geocercas, clientes, referencias)
 * @param {Array} puntos - Array de puntos del recorrido
 * @param {number} tiempo - Tiempo mínimo para considerar una estadía (minutos)
 * @param {number} distancia - Distancia máxima para considerar una estadía (metros)
 * @param {number} tipo - Tipo de estadía (0: geocercas, 1: clientes, 2: referencias, -1: indeterminadas)
 * @returns {Array} Puntos de inicio y fin de cada estadía
 */
export const obtenerEstadiasPuntoAPunto = (puntos, tiempo = 5, distancia = 50, tipo = 0) => {
  try {
    const respuesta = [];
    let puntoInicio = null;
    let metros = 0;

    const compareValue = tipo === 1 ? "0" : "";
    let compareTo = "";
    let compareToini = "";

    puntos = puntos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    puntos.forEach((punto, contador) => {
      const compare = grcConcat(punto);

      // Determinar valor a comparar según el tipo
      switch (tipo) {
        case 0:
          compareTo = compare.geocercas;
          break;
        case 1:
          compareTo = compare.clientes;
          break;
        case 2:
          compareTo = compare.referencias;
          break;
        default:
          // Caso combinado/mixto - IGUAL QUE C#
          if (compare.geocercas) {
            compareTo = compare.geocercas;
          }
          if (!compareTo || compareTo === "Traslado") {
            if (compare.clientes) {
              compareTo = compare.clientes;
            }
          }
          if (!compareTo || compareTo === "Traslado") {
            if (compare.referencias) {
              compareTo = compare.referencias;
            }
          }
          break;
      }

      metros = 0;

      if (compareTo !== compareValue && compareTo !== "Traslado") {
        if (!puntoInicio) {
          puntoInicio = punto;

          const compareIni = grcConcat(puntoInicio);

          switch (tipo) {
            case 0:
              compareToini = compareIni.geocercas;
              break;
            case 1:
              compareToini = compareIni.clientes;
              break;
            case 2:
              compareToini = compareIni.referencias;
              break;
            default:
              if (compareIni.geocercas) {
                compareToini = compareIni.geocercas;
              }
              if (!compareToini || compareToini === "Traslado") {
                if (compareIni.clientes) {
                  compareToini = compareIni.clientes;
                }
              }
              if (!compareToini || compareToini === "Traslado") {
                if (compareIni.referencias) {
                  compareToini = compareIni.referencias;
                }
              }
              break;
          }
          return; // Continuar con el siguiente punto
        }
      }
      else if (tipo === -1) {
        // ✅ ESTADÍAS INDETERMINADAS - IGUAL QUE C#
        if (!puntoInicio) {
          puntoInicio = punto;

          const compareIni = grcConcat(puntoInicio);

          compareToini = compareIni.geocercas;
          if (compareToini === "Traslado") {
            if (compareIni.clientes) {
              compareToini = compareIni.clientes;
            }
          }
          if (compareToini === "Traslado") {
            if (compareIni.referencias) {
              compareToini = compareIni.referencias;
            }
          }
          if (compareToini === "Traslado") {
            compareToini = "Indeterminado";
          }
          return; // Continuar con el siguiente punto
        }
      }

      if (puntoInicio && compareToini !== compareTo) {
        const latitudIni = puntoInicio.location?.coordinates?.[1] || puntoInicio.latitud || 0;
        const longitudIni = puntoInicio.location?.coordinates?.[0] || puntoInicio.longitud || 0;
        const latitudPunto = punto.location?.coordinates?.[1] || punto.latitud || 0;
        const longitudPunto = punto.location?.coordinates?.[0] || punto.longitud || 0;

        metros = calcularDistancia(longitudIni, latitudIni, longitudPunto, longitudPunto);

        // Verificar si cumple la restricción de distancia
        if (distancia > metros) {
          puntoInicio = null;
          compareToini = "";
          return; // Continuar con el siguiente punto
        }

        // Verificar el tiempo transcurrido
        const diff = (new Date(punto.fecha) - new Date(puntoInicio.fecha)) / (1000 * 60); // Minutos
        if (diff < tiempo || diff === 0) {
          puntoInicio = null;
          compareToini = "";
          return; // Continuar con el siguiente punto
        }

        // Marcar como estadía punto a punto
        puntoInicio.tipoReporteEstadia = 3;
        punto.tipoReporteEstadia = 3;

        // Agregar a la respuesta
        respuesta.push({ ...puntoInicio });
        respuesta.push({ ...punto });

        puntoInicio = null;
        compareToini = "";
      }
    });

    return respuesta;
  } catch (error) {
    console.error('Error en obtenerEstadiasPuntoAPunto:', error);
    return [];
  }
};

/**
 * ✅ FUNCIÓN PARA APLICAR CONFIGURACIÓN DE ALERTAS (FALTABA)
 * @param {Array} estadias - Lista de estadías
 * @param {Array} configuraciones - Configuraciones de vehículos
 * @returns {Array} Estadías con configuración aplicada
 */
export const aplicarConfiguracionAlertas = async (estadias, configuraciones) => {
  if (!configuraciones || configuraciones.length === 0) return estadias;

  return estadias.map(estadia => {
    const config = configuraciones.find(c => c.vehiculoid === estadia.vehiculoId);
    if (config && config.alertaDetenido) {
      estadia.alertaDetenido = parseInt(config.alertaDetenido) || 0;
    }
    return estadia;
  });
};

/**
 * Función principal para calcular estadías combinando los diferentes métodos
 * @param {Array} recorridos - Lista de puntos del recorrido
 * @param {Object} opciones - Opciones de cálculo
 * @returns {Array} Lista de estadías calculadas
 */
export const calcularEstadias = (recorridos, opciones = {
  tipoReporte: 0,    // 0: detenidos, 1: encendido/apagado, 2: ambos, 3: punto a punto
  tipoVista: 0,      // 0: geocercas, 1: clientes, 2: referencias, -1: indeterminadas
  tiempoEstancia: 5, // minutos
  distanciaEstancia: 50, // metros
  uids_choferes: []  // ✅ AGREGAR FILTRO DE CHOFERES
}) => {
  if (!recorridos || recorridos.length === 0) {
    return [];
  }

  let estadias = [];

  try {
    // Ordenar recorridos por fecha
    const recorridosOrdenados = [...recorridos].sort((a, b) =>
      new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
    );

    // ✅ FILTRO POR ENCENDIDO/APAGADO COMO EN C#
    let recorridosFiltrados = recorridosOrdenados;
    if (opciones.encendido_o_apagado && opciones.encendido_o_apagado !== "Todo") {
      recorridosFiltrados = recorridosOrdenados.filter(w =>
        w.tipo === 3 || w.tipo === 4 || w.tipo === 5 || w.tipo === 6
      );
    }

    // Calcular estadías según el tipo de reporte
    if (opciones.tipoReporte === 0) {
      // Estadías por detenido
      estadias = obtenerEstadiasDetenido(recorridosFiltrados, opciones.tiempoEstancia, opciones.distanciaEstancia);
    }
    else if (opciones.tipoReporte === 1) {
      // Estadías por encendido/apagado
      estadias = obtenerEstadiasEncendidoApagado(recorridosFiltrados, opciones.tiempoEstancia, opciones.distanciaEstancia);
    }
    else if (opciones.tipoReporte === 2) {
      // Combinar ambos tipos
      const estadiasDetenido = obtenerEstadiasDetenido(recorridosFiltrados, opciones.tiempoEstancia, opciones.distanciaEstancia);
      const estadiasEncendido = obtenerEstadiasEncendidoApagado(recorridosFiltrados, opciones.tiempoEstancia, opciones.distanciaEstancia);
      estadias = [...estadiasDetenido, ...estadiasEncendido];
    }
    else {
      // Estadías punto a punto
      estadias = obtenerEstadiasPuntoAPunto(recorridosFiltrados, opciones.tiempoEstancia, opciones.distanciaEstancia, opciones.tipoVista);
    }

    // Procesar los resultados para tener un formato más útil
    const estadiasFormatted = [];

    for (let i = 0; i < estadias.length; i += 2) {
      const puntoInicio = estadias[i];
      const puntoFin = estadias[i + 1];

      if (!puntoInicio || !puntoFin) continue;

      const fechaInicio = new Date(puntoInicio.fecha);
      const fechaFin = new Date(puntoFin.fecha);
      const diffMs = fechaFin - fechaInicio;

      // Calcular horas, minutos y segundos
      const horas = Math.floor(diffMs / 3600000).toString().padStart(2, '0');
      const minutos = Math.floor((diffMs % 3600000) / 60000).toString().padStart(2, '0');
      const segundos = Math.floor((diffMs % 60000) / 1000).toString().padStart(2, '0');

      // Procesar geocercas, clientes y referencias
      const compareInicio = grcConcat(puntoInicio);
      const compareFin = grcConcat(puntoFin);

      let PI = "";
      let PF = "";
      let tipo = Constantes.INDETERMINADO;

      // ✅ DETERMINAR TIPO Y NOMBRE SEGÚN tipoVista - IGUAL QUE C#
      switch (opciones.tipoVista) {
        case 0:
          PI = compareInicio.geocercas || "Indeterminado";
          PF = compareFin.geocercas || "Indeterminado";
          tipo = Constantes.GEOCERCA;
          break;
        case 1:
          PI = compareInicio.clientes || "Indeterminado";
          PF = compareFin.clientes || "Indeterminado";
          tipo = Constantes.CLIENTE;
          break;
        case 2:
          PI = compareInicio.referencias || "Indeterminado";
          PF = compareFin.referencias || "Indeterminado";
          tipo = Constantes.REFERENCIA;
          break;
        default:
          // ✅ LÓGICA COMBINADA IGUAL QUE C#
          let inicio = compareInicio.geocercas;
          tipo = Constantes.GEOCERCA;

          if (!inicio || inicio === "Traslado") {
            inicio = compareInicio.clientes;
            tipo = Constantes.CLIENTE;
          }
          if (!inicio || inicio === "Traslado") {
            inicio = compareInicio.referencias;
            tipo = Constantes.REFERENCIA;
          }
          if (!inicio) {
            inicio = "Indeterminado";
            tipo = Constantes.INDETERMINADO;
          }

          let fin = compareFin.geocercas;
          if (!fin || fin === "Traslado") {
            fin = compareFin.clientes;
          }
          if (!fin || fin === "Traslado") {
            fin = compareFin.referencias;
          }
          if (!fin) {
            fin = "Indeterminado";
          }

          PI = inicio;
          PF = fin;
          break;
      }

      // ✅ FILTROS DE "TRASLADO" COMO EN C#
      let agregar = false;
      if (opciones.tipoVista === -1) {
        // Solo indeterminadas: ambos deben ser "Indeterminado"
        if (PI === "Indeterminado" && PF === "Indeterminado") {
          agregar = true;
        }
      } else {
        // ✅ FILTROS ESPECÍFICOS POR TIPO DE VISTA
        if (opciones.tipoVista === 0) {
          // Geocercas: saltar si es "Traslado"
          if (compareInicio.geocercas === "Traslado") {
            continue;
          }
        } else if (opciones.tipoVista === 1) {
          // Clientes: saltar si es "Traslado"  
          if (compareInicio.clientes === "Traslado") {
            continue;
          }
        } else if (opciones.tipoVista === 2) {
          // Referencias: saltar si está vacío o es "Traslado"
          if (!compareInicio.referencias || compareInicio.referencias === "Traslado") {
            continue;
          }
        }
        agregar = true;
      }

      if (!agregar) continue;

      // Calcular tiempos según motor
      let tiempoMotorEncendido = 0;
      let tiempoMotorApagado = 0;

      if (puntoInicio.tipoMotor === 1) { // Encendido
        tiempoMotorEncendido = diffMs / 1000; // segundos
      } else if (puntoInicio.tipoMotor === 0) { // Apagado
        tiempoMotorApagado = diffMs / 1000; // segundos
      }

      // ✅ FORMATEO DE FECHA LOCAL COMO EN C#
      const formatLocalDate = (date) => {
        // Convertir a formato local yyyy/MM/dd HH:mm:ss como en C#
        return date.toLocaleString('sv-SE').replace('T', ' ').replace(/-/g, '/');
      };

      // Crear objeto de estadia
      const estadia = {
        uid_vehiculo: puntoFin.vehiculoUid || puntoFin.VehiculoUid,
        no_economico: puntoFin.vehiculos?.noEconomico || puntoFin.Vehiculos?.NoEconomico || "",
        alias: puntoFin.vehiculos?.alias || puntoFin.Vehiculos?.Alias || "",
        chofer: puntoInicio.chofer?.nombreChofer || puntoInicio.Chofer?.NombreChofer || "",
        fecha_inicio: formatLocalDate(fechaInicio),
        fecha_fin: formatLocalDate(fechaFin),
        tiempo: `${horas}:${minutos}:${segundos}`,
        tiempo_segundos: diffMs / 1000,
        tiempo_encendido: tiempoMotorEncendido,
        tiempo_apagado: tiempoMotorApagado,
        punto_inicio: PI === "Traslado" ? "Indeterminado" : PI,
        punto_fin: PF === "Traslado" ? "Indeterminado" : PF,
        tipo_reporte_estadia: puntoFin.tipoReporteEstadia || 0,
        tipo_reporte: puntoFin.tipoReporteEstadia === 0 ? "Detenidos" :
          puntoFin.tipoReporteEstadia === 3 ? "Punto a punto" : "A/E",
        tipo: tipo,
        evento_inicio: puntoInicio.nombreEvento || "",
        evento_fin: puntoFin.nombreEvento || "",
        tipo_motor_inicio: puntoInicio.tipoMotor,
        tipo_motor_fin: puntoFin.tipoMotor,
        latitud: puntoInicio.latitud || puntoInicio.Latitud || 0,
        longitud: puntoInicio.longitud || puntoInicio.Longitud || 0,
        // ✅ AGREGAR CAMPOS ADICIONALES
        choferUid: puntoInicio.choferUid || puntoInicio.ChoferUid || "",
        vehiculoId: puntoInicio.vehiculoId || puntoInicio.VehiculoId || 0
      };

      estadiasFormatted.push(estadia);
    }

    // ✅ FILTRO POR CHOFERES AL FINAL (COMO EN C#)
    let resultado = estadiasFormatted;
    if (opciones.uids_choferes && opciones.uids_choferes.length > 0) {
      resultado = estadiasFormatted.filter(estadia =>
        opciones.uids_choferes.includes(estadia.choferUid)
      );
    }

    return resultado;

  } catch (error) {
    console.error('Error al calcular estadías:', error);
    return [];
  }
};

/**
 * Agrega información concatenada de geocercas, clientes y referencias a cada punto
 * @param {Array} recorridos - Lista de puntos del recorrido
 * @returns {Array} Recorridos con campos adicionales
 */
export const procesarRecorridos = (recorridos) => {
  if (!recorridos || recorridos.length === 0) {
    return [];
  }

  return recorridos.map((item, index) => {
    //console.log(item);
    // Crear listas para almacenar los valores
    const direccion = [];
    const geocercas = [];
    const clientes = [];
    const referencias = [];

    let fechaFormateada = item.fecha;
    if (item.fecha && typeof item.fecha === 'string' && item.fecha.includes('T')) {
      // ✅ CORRECCIÓN: Convertir correctamente de UTC a zona horaria local
      const fechaUTC = new Date(item.fecha);

      // Si necesitas ajustar manualmente la zona horaria (México es UTC-6 en horario estándar, UTC-5 en horario de verano)
      // Obtener el offset de zona horaria del navegador
      const offsetMinutos = fechaUTC.getTimezoneOffset();
      const fechaLocal = fechaUTC;////new Date(fechaUTC.getTime() - (offsetMinutos * 60 * 1000));

      // ✅ ALTERNATIVA: Si sabes que necesitas restar 6 horas específicamente (México)
      //const fechaLocal = new Date(fechaUTC.getTime() - (6 * 60 * 60 * 1000));

      const year = fechaLocal.getFullYear();
      const month = String(fechaLocal.getMonth() + 1).padStart(2, '0');
      const day = String(fechaLocal.getDate()).padStart(2, '0');
      const hours = String(fechaLocal.getHours()).padStart(2, '0');
      const minutes = String(fechaLocal.getMinutes()).padStart(2, '0');
      const seconds = String(fechaLocal.getSeconds()).padStart(2, '0');

      fechaFormateada = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;

      //console.log(`[procesarRecorridos] Fecha original: ${item.fecha}, Fecha local: ${fechaFormateada}`);
    }

    // ✅ PROCESAR GEOCERCAS - COMO EN C#
    const geocercasObj = item.geocercas || item.Geocercas;
    if (geocercasObj) {
      const asignadas = geocercasObj.geocercasasignadas || geocercasObj.Geocercasasignadas;
      if (asignadas && Array.isArray(asignadas)) {
        asignadas.forEach(g => {
          const uid = g.geocercaUid || g.GeocercaUid;
          const nombre = g.nombreGeocerca || g.NombreGeocerca;

          // ✅ CONDICIONES ESTRICTAS: UID válido, nombre válido, NO "Traslado"
          if (uid !== "0" &&
            nombre &&
            nombre.trim() !== '' &&
            nombre.trim().toLowerCase() !== "traslado") {
            direccion.push(nombre);
            geocercas.push(nombre);
          }
        });
      }
    }

    // ✅ PROCESAR CLIENTES - EXCLUIR "TRASLADO"
    const clientesArr = item.clientes || item.Clientes;
    if (clientesArr && Array.isArray(clientesArr)) {
      clientesArr.forEach(i => {
        const numTienda = i.Num_Tienda || i.num_Tienda || 0;
        const nombre = i.Nombreclientes || i.nombreclientes || i.nombreClientes;

        if (numTienda !== 0 &&
          nombre &&
          nombre.trim() !== '' &&
          nombre.trim().toLowerCase() !== "traslado") {
          direccion.push(nombre);
          clientes.push(nombre);
        }
      });
    }

    // ✅ PROCESAR REFERENCIAS - EXCLUIR "TRASLADO"
    const referenciasArr = item.referencias || item.Referencias;
    if (referenciasArr && Array.isArray(referenciasArr)) {
      referenciasArr.forEach(i => {
        const uid = i.referenciaUid || i.ReferenciaUid;
        const nombre = i.nombreReferencia || i.NombreReferencia;

        if (uid !== "0" &&
          nombre &&
          nombre.trim() !== '' &&
          nombre.trim().toLowerCase() !== "traslado") {
          direccion.push(nombre);
          referencias.push(nombre);
        }
      });
    }

    // Asignar campos concatenados
    const direccionApi = (item.direccion || item.Direccion || item.address || '').trim();
    const direccionCalculada =
      direccionApi !== ''
        ? direccionApi
        : direccion.length > 0
          ? direccion.join(', ')
          : "Dirección no disponible";

    if (index < 3) {
      //console.log(`[procesarRecorridos] punto ${index} | direccion entrada: ${direccionApi || 'N/A'} | direccion asignada: ${direccionCalculada}`);
    }

    return {
      ...item,
      fecha: fechaFormateada,
      direccion: direccionCalculada,
      geocercas_concat: geocercas.join(','),
      clientes_concat: clientes.join(','),
      referencias_concat: referencias.join(','),
      // ✅ MANTENER ESTRUCTURA CORRECTA DEL CHOFER
      uid_vehiculo: item.vehiculoUid, // Agregar campo directo
      no_economico: item.vehiculos?.noEconomico || 'N/A',
      alias: item.vehiculos?.alias || 'N/A'
    };
  });
};

/**
 * Asigna la velocidad máxima a los vehículos según configuración
 * @param {Array} recorridos - Lista de puntos del recorrido
 * @param {Object} parametrosConfig - Configuración de vehículos
 * @returns {Array} Recorridos con velocidad máxima asignada
 */
export const asignarVelocidadMaxima = (recorridos, parametrosConfig = []) => {
  if (!recorridos || recorridos.length === 0) {
    return [];
  }

  return recorridos.map(item => {
    if (!item.vehiculos) {
      item.vehiculos = {};
    }

    // Buscar configuración para este vehículo
    const conf = parametrosConfig.find(w => w.vehiculoUid === item.vehiculoUid);

    // Asignar velocidad máxima
    item.vehiculos.velocidadMaxima = conf ? parseFloat(conf.excesoVelocidad) : 90;

    return item;
  });
};

// ✅ FUNCIÓN AUXILIAR PARA VALIDAR FILTRO POR TIPO DE VISTA
function cumpleFiltroPorTipoVista(ubicacion, categoria, tipoVista) {
  switch (tipoVista) {
    case 0: // GEOCERCAS
      return ubicacion !== 'Indeterminado' &&
        ubicacion !== 'Traslado' &&
        categoria === 'Geocercas';

    case 1: // CLIENTES
      return ubicacion !== 'Indeterminado' &&
        ubicacion !== 'Traslado' &&
        categoria === 'Clientes';

    case 2: // REFERENCIAS
      return ubicacion !== 'Indeterminado' &&
        ubicacion !== 'Traslado' &&
        categoria === 'Referencias';

    case 3: // TODOS
      return true; // Incluir todas las ubicaciones

    case -1: // INDETERMINADOS
      return ubicacion === 'Indeterminado';

    default:
      return true;
  }
}


const obtenerUbicacion = (registro) => {
  let ubicacionesEncontradas = [];

  // console.log(`[obtenerUbicacion] 📍 Analizando registro:`, {
  //   fecha: registro.fecha,
  //   vehiculo: registro.vehiculoUid,
  //   tieneGeocercas: !!registro.geocercas,
  //   tieneClientes: !!registro.clientes,  
  //   tieneReferencias: !!registro.referencias
  // });

  // ✅ PRIORIDAD 1: GEOCERCAS (OBTENER TODAS LAS VÁLIDAS)
  const geocercasObj = registro.geocercas || registro.Geocercas;
  if (geocercasObj) {
    const asignadas = geocercasObj.geocercasasignadas || geocercasObj.Geocercasasignadas;
    if (asignadas && Array.isArray(asignadas)) {

      const geocercasValidas = asignadas
        .filter(g => {
          const uid = g.geocercaUid || g.GeocercaUid;
          const nombre = g.nombreGeocerca || g.NombreGeocerca;
          return uid && uid !== "0" && nombre && nombre.trim() !== '';
        })
        .map(g => {
          const nombre = (g.nombreGeocerca || g.NombreGeocerca).trim();
          return {
            ubicacion: nombre,
            categoria: nombre === "Traslado" ? 'Traslados' : 'Geocercas',
            uid: g.geocercaUid || g.GeocercaUid
          };
        });

      ubicacionesEncontradas.push(...geocercasValidas);
      // console.log(`[obtenerUbicacion] ✅ ${geocercasValidas.length} geocercas válidas encontradas`);
    }
  }

  // ✅ PRIORIDAD 2: CLIENTES (OBTENER TODOS LOS VÁLIDOS)
  const clientesArr = registro.clientes || registro.Clientes;
  if (clientesArr && Array.isArray(clientesArr)) {

    const clientesValidos = clientesArr
      .filter(c => {
        const nombre = c.Nombreclientes || c.nombreclientes || c.nombreClientes;
        return nombre &&
          nombre.trim() !== '' &&
          nombre.trim().toLowerCase() !== "traslado";
      })
      .map(c => ({
        ubicacion: (c.Nombreclientes || c.nombreclientes || c.nombreClientes).trim(),
        categoria: 'Clientes',
        numTienda: c.Num_Tienda || c.num_Tienda || 0
      }));

    ubicacionesEncontradas.push(...clientesValidos);
    // console.log(`[obtenerUbicacion] ✅ ${clientesValidos.length} clientes válidos encontrados`);
  }

  // ✅ PRIORIDAD 3: REFERENCIAS (OBTENER TODAS LAS VÁLIDAS)
  const referenciasArr = registro.referencias || registro.Referencias;
  if (referenciasArr && Array.isArray(referenciasArr)) {

    const referenciasValidas = referenciasArr
      .filter(r => {
        const nombre = r.nombreReferencia || r.NombreReferencia;
        return nombre &&
          nombre.trim() !== '' &&
          nombre.trim().toLowerCase() !== "traslado";
      })
      .map(r => ({
        ubicacion: (r.nombreReferencia || r.NombreReferencia).trim(),
        categoria: 'Referencias',
        uid: r.referenciaUid || r.ReferenciaUid
      }));

    ubicacionesEncontradas.push(...referenciasValidas);
    // console.log(`[obtenerUbicacion] ✅ ${referenciasValidas.length} referencias válidas encontradas`);
  }

  // ✅ FALLBACK: Campos concatenados
  if (ubicacionesEncontradas.length === 0) {
    if (registro.referencias_concat && registro.referencias_concat.trim() !== '' && registro.referencias_concat.trim().toLowerCase() !== 'traslado') {
      ubicacionesEncontradas.push({
        ubicacion: registro.referencias_concat.trim(),
        categoria: 'Referencias'
      });
      // console.log(`[obtenerUbicacion] ✅ Usando referencias_concat: ${registro.referencias_concat}`);
    } else if (registro.clientes_concat && registro.clientes_concat.trim() !== '' && registro.clientes_concat.trim().toLowerCase() !== 'traslado') {
      ubicacionesEncontradas.push({
        ubicacion: registro.clientes_concat.trim(),
        categoria: 'Clientes'
      });
      // console.log(`[obtenerUbicacion] ✅ Usando clientes_concat: ${registro.clientes_concat}`);
    }
  }

  // ✅ SI NO HAY UBICACIONES VÁLIDAS
  if (ubicacionesEncontradas.length === 0) {
    ubicacionesEncontradas.push({
      ubicacion: 'Indeterminado',
      categoria: 'Indeterminado'
    });
    // console.log(`[obtenerUbicacion] ⚠️ No se encontraron ubicaciones válidas, usando 'Indeterminado'`);
  }

  // console.log(`[obtenerUbicacion] 🎯 ESTRATEGIA SEPARAR - ${ubicacionesEncontradas.length} ubicaciones:`,
  //   ubicacionesEncontradas.map(u => `${u.ubicacion} (${u.categoria})`).join(', '));

  // ✅ RETORNAR EL OBJETO CORRECTO
  return { ubicacionesMultiples: ubicacionesEncontradas };
};

// ✅ FUNCIÓN AUXILIAR PARA CONVERTIR FECHAS UTC A LOCAL
const convertirFechaLocal = (fechaStr) => {
  try {
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) {
      //console.warn("Fecha inválida:", fechaStr);
      return new Date();
    }
    return fecha;
  } catch (error) {
    //console.warn("Error al convertir fecha:", fechaStr, error);
    return new Date();
  }
};

/**
 * ✅ FUNCIÓN CORREGIDA: Calcula estadías para múltiples tipos de vista
 * @param {Array} recorridos - Datos de recorridos
 * @param {Object} filtros - Filtros aplicados
 * @returns {Array} Estadías calculadas
 */
export const calcularEstadiasMultiples = (recorridos, filtros) => {
  const totalRegistros = Array.isArray(recorridos) ? recorridos.length : 0;
  const sampleRegistros = (recorridos || []).slice(0, 5).map((registro, index) => ({
    index,
    vehiculoUid: registro?.vehiculoUid || registro?.vehiculos?.vehiculoUid,
    fecha: registro?.fecha || registro?.fechaCorta || registro?.fecha_inicio,
    tipo: registro?.tipo,
    tipoMotor: registro?.tipoMotor ?? registro?.tipo_motor,
    nombreEvento: registro?.nombreEvento,
    velocidad: registro?.velocidad,
    latitud: registro?.latitud,
    longitud: registro?.longitud
  }));

  //console.groupCollapsed(`[calcularEstadiasMultiples] Inicio | total=${totalRegistros}`);
  //console.log("Filtros recibidos:", filtros);
  //console.log("Muestra de registros:", sampleRegistros);
  //console.groupEnd();

  if (!recorridos || recorridos.length === 0) {
    return [];
  }

  const estadias = [];
  const tiempoMinimoEstadia = (filtros.tiempo_estancia || 0) * 60; // Convertir minutos a segundos
  //console.log("[calcularEstadiasMultiples] Tiempo mínimo estadía:", tiempoMinimoEstadia, "segundos");



  // ✅ AGRUPAR POR VEHÍCULO
  const vehiculosGroup = recorridos.reduce((acc, registro) => {
    const vehiculoId = registro.vehiculoUid || registro.vehiculos?.vehiculoUid || 'N/A';
    if (!acc[vehiculoId]) {
      acc[vehiculoId] = [];
    }
    acc[vehiculoId].push(registro);
    return acc;
  }, {});

  //console.log("[calcularEstadiasMultiples] Vehículos agrupados:", Object.keys(vehiculosGroup).length);

  // ✅ PROCESAR CADA VEHÍCULO
  Object.entries(vehiculosGroup).forEach(([vehiculoUid, registros]) => {
    const aliasVehiculo = registros[0]?.vehiculos?.alias || vehiculoUid;
    //console.log(`[${aliasVehiculo}] Procesando ${registros.length} registros`);

    // Ordenar registros por fecha
    registros.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    // ✅ VARIABLES PARA RASTREAR ESTADÍAS
    let estadiaInicio = null;

    // ✅ FUNCIÓN PARA OBTENER UBICACIÓN CON LOGS DETALLADOS
    const obtenerUbicacion_respaldo = (registro) => {
      let ubicacion = 'Indeterminado';
      let categoria = 'Indeterminado';

      // console.log(`[obtenerUbicacion] 📍 Analizando registro:`, {
      //   fecha: registro.fecha,
      //   vehiculo: registro.vehiculoUid,
      //   geocercas: registro.geocercas
      // });

      // ✅ PRIORIDAD 1: GEOCERCAS (FILTROS MENOS RESTRICTIVOS)
      const geocercasObj = registro.geocercas || registro.Geocercas;
      if (geocercasObj) {
        const asignadas = geocercasObj.geocercasasignadas || geocercasObj.Geocercasasignadas;
        if (asignadas && Array.isArray(asignadas)) {
          //console.log(`[obtenerUbicacion] ✅ Geocercas encontradas:`, asignadas.length);

          // ✅ BUSCAR GEOCERCA VÁLIDA (PERMITIR TODAS MENOS UID "0")
          const geocercaValida = asignadas.find(g => {
            const uid = g.geocercaUid || g.GeocercaUid;
            const nombre = g.nombreGeocerca || g.NombreGeocerca;

            const esValida = uid && uid !== "0" && nombre && nombre.trim() !== '';
            // console.log(`[obtenerUbicacion] Validando geocerca:`, {
            //   uid, nombre, esValida
            // });

            return esValida;
          });

          if (geocercaValida) {
            const nombre = geocercaValida.nombreGeocerca || geocercaValida.NombreGeocerca;
            ubicacion = nombre.trim();
            categoria = nombre === "Traslado" ? 'Traslados' : 'Geocercas';

            // console.log(`[obtenerUbicacion] 🎯 GEOCERCA SELECCIONADA:`, {
            //   ubicacion, categoria, uid: geocercaValida.geocercaUid
            // });
            return { ubicacion, categoria };
          }
        }
      }

      // ✅ PRIORIDAD 2: CLIENTES - CORREGIDO
      const clientesArr = registro.clientes || registro.Clientes;
      if (clientesArr && Array.isArray(clientesArr)) {
        // console.log(`[obtenerUbicacion] 👥 Clientes encontrados:`, clientesArr.length);

        // clientesArr.forEach((c, index) => {
        //   console.log(`[obtenerUbicacion] Cliente ${index}:`, {
        //     Num_Tienda: c.Num_Tienda,
        //     Nombreclientes: c.Nombreclientes || c.nombreclientes || c.nombreClientes,
        //     estructuraCompleta: c
        //   });
        // });

        const clienteValido = clientesArr.find(c => {
          const numTienda = c.Num_Tienda || c.num_Tienda || 0;
          const nombre = c.Nombreclientes || c.nombreclientes || c.nombreClientes;

          // ✅ VALIDACIÓN MENOS RESTRICTIVA PARA CLIENTES
          const esValido = nombre &&
            nombre.trim() !== '' &&
            nombre.trim().toLowerCase() !== "traslado";

          // console.log(`[obtenerUbicacion] Validando cliente:`, {
          //   numTienda, nombre, esValido
          // });

          return esValido;
        });

        if (clienteValido) {
          const nombre = clienteValido.Nombreclientes || clienteValido.nombreclientes || clienteValido.nombreClientes;
          ubicacion = nombre.trim();
          categoria = 'Clientes';

          // console.log(`[obtenerUbicacion] 🎯 CLIENTE SELECCIONADO:`, {
          //   ubicacion, categoria
          // });
          return { ubicacion, categoria };
        }
      }

      // ✅ PRIORIDAD 3: REFERENCIAS - CORREGIDO
      const referenciasArr = registro.referencias || registro.Referencias;
      if (referenciasArr && Array.isArray(referenciasArr)) {
        //console.log(`[obtenerUbicacion] 📍 Referencias encontradas:`, referenciasArr.length);

        // referenciasArr.forEach((r, index) => {
        //   console.log(`[obtenerUbicacion] Referencia ${index}:`, {
        //     referenciaUid: r.referenciaUid || r.ReferenciaUid,
        //     nombreReferencia: r.nombreReferencia || r.NombreReferencia,
        //     estructuraCompleta: r
        //   });
        // });

        const referenciaValida = referenciasArr.find(r => {
          const uid = r.referenciaUid || r.ReferenciaUid;
          const nombre = r.nombreReferencia || r.NombreReferencia;

          // ✅ VALIDACIÓN MENOS RESTRICTIVA PARA REFERENCIAS
          const esValida = nombre &&
            nombre.trim() !== '' &&
            nombre.trim().toLowerCase() !== "traslado";

          // console.log(`[obtenerUbicacion] Validando referencia:`, {
          //   uid, nombre, esValida
          // });

          return esValida;
        });

        if (referenciaValida) {
          const nombre = referenciaValida.nombreReferencia || referenciaValida.NombreReferencia;
          ubicacion = nombre.trim();
          categoria = 'Referencias';

          // console.log(`[obtenerUbicacion] 🎯 REFERENCIA SELECCIONADA:`, {
          //   ubicacion, categoria
          // });
          return { ubicacion, categoria };
        }
      }

      // ✅ PRIORIDAD 4: USAR CAMPOS CONCATENADOS COMO FALLBACK
      if (registro.referencias_concat &&
        registro.referencias_concat.trim() !== '' &&
        registro.referencias_concat.trim().toLowerCase() !== 'traslado') {
        ubicacion = registro.referencias_concat.trim();
        categoria = 'Referencias';
        // console.log(`[obtenerUbicacion] 🎯 REFERENCIAS_CONCAT UTILIZADA:`, {
        //   ubicacion, categoria
        // });
        return { ubicacion, categoria };
      }

      if (registro.clientes_concat &&
        registro.clientes_concat.trim() !== '' &&
        registro.clientes_concat.trim().toLowerCase() !== 'traslado') {
        ubicacion = registro.clientes_concat.trim();
        categoria = 'Clientes';
        // console.log(`[obtenerUbicacion] 🎯 CLIENTES_CONCAT UTILIZADA:`, {
        //   ubicacion, categoria
        // });
        return { ubicacion, categoria };
      }

      //console.log(`[obtenerUbicacion] ❌ No se encontró ubicación válida`);
      return { ubicacion: 'Indeterminado', categoria: 'Indeterminado' };
    };

    const obtenerUbicacion = (registro) => {
      let ubicacionesEncontradas = [];

      // console.log(`[obtenerUbicacion] 📍 Analizando registro:`, {
      //   fecha: registro.fecha,
      //   vehiculo: registro.vehiculoUid,
      //   tieneGeocercas: !!registro.geocercas,
      //   tieneClientes: !!registro.clientes,
      //   tieneReferencias: !!registro.referencias
      // });

      // ✅ PRIORIDAD 1: GEOCERCAS (OBTENER TODAS LAS VÁLIDAS)
      const geocercasObj = registro.geocercas || registro.Geocercas;
      if (geocercasObj) {
        const asignadas = geocercasObj.geocercasasignadas || geocercasObj.Geocercasasignadas;
        if (asignadas && Array.isArray(asignadas)) {

          const geocercasValidas = asignadas
            .filter(g => {
              const uid = g.geocercaUid || g.GeocercaUid;
              const nombre = g.nombreGeocerca || g.NombreGeocerca;
              return uid && uid !== "0" && nombre && nombre.trim() !== '';
            })
            .map(g => {
              const nombre = (g.nombreGeocerca || g.NombreGeocerca).trim();
              return {
                ubicacion: nombre,
                categoria: nombre === "Traslado" ? 'Traslados' : 'Geocercas',
                uid: g.geocercaUid || g.GeocercaUid
              };
            });

          ubicacionesEncontradas.push(...geocercasValidas);
          //console.log(`[obtenerUbicacion] ✅ ${geocercasValidas.length} geocercas válidas encontradas`);
        }
      }

      // ✅ PRIORIDAD 2: CLIENTES (OBTENER TODOS LOS VÁLIDOS)
      const clientesArr = registro.clientes || registro.Clientes;
      if (clientesArr && Array.isArray(clientesArr)) {

        const clientesValidos = clientesArr
          .filter(c => {
            const nombre = c.Nombreclientes || c.nombreclientes || c.nombreClientes;
            return nombre &&
              nombre.trim() !== '' &&
              nombre.trim().toLowerCase() !== "traslado";
          })
          .map(c => ({
            ubicacion: (c.Nombreclientes || c.nombreclientes || c.nombreClientes).trim(),
            categoria: 'Clientes',
            numTienda: c.Num_Tienda || c.num_Tienda || 0
          }));

        ubicacionesEncontradas.push(...clientesValidos);
        //console.log(`[obtenerUbicacion] ✅ ${clientesValidos.length} clientes válidos encontrados`);
      }

      // ✅ PRIORIDAD 3: REFERENCIAS (OBTENER TODAS LAS VÁLIDAS)
      const referenciasArr = registro.referencias || registro.Referencias;
      if (referenciasArr && Array.isArray(referenciasArr)) {

        const referenciasValidas = referenciasArr
          .filter(r => {
            const nombre = r.nombreReferencia || r.NombreReferencia;
            return nombre &&
              nombre.trim() !== '' &&
              nombre.trim().toLowerCase() !== "traslado";
          })
          .map(r => ({
            ubicacion: (r.nombreReferencia || r.NombreReferencia).trim(),
            categoria: 'Referencias',
            uid: r.referenciaUid || r.ReferenciaUid
          }));

        ubicacionesEncontradas.push(...referenciasValidas);
        //console.log(`[obtenerUbicacion] ✅ ${referenciasValidas.length} referencias válidas encontradas`);
      }

      // ✅ FALLBACK: Campos concatenados
      if (ubicacionesEncontradas.length === 0) {
        if (registro.referencias_concat && registro.referencias_concat.trim() !== '' && registro.referencias_concat.trim().toLowerCase() !== 'traslado') {
          ubicacionesEncontradas.push({
            ubicacion: registro.referencias_concat.trim(),
            categoria: 'Referencias'
          });
          //console.log(`[obtenerUbicacion] ✅ Usando referencias_concat: ${registro.referencias_concat}`);
        } else if (registro.clientes_concat && registro.clientes_concat.trim() !== '' && registro.clientes_concat.trim().toLowerCase() !== 'traslado') {
          ubicacionesEncontradas.push({
            ubicacion: registro.clientes_concat.trim(),
            categoria: 'Clientes'
          });
          //console.log(`[obtenerUbicacion] ✅ Usando clientes_concat: ${registro.clientes_concat}`);
        }
      }

      // ✅ SI NO HAY UBICACIONES VÁLIDAS
      if (ubicacionesEncontradas.length === 0) {
        ubicacionesEncontradas.push({
          ubicacion: 'Indeterminado',
          categoria: 'Indeterminado'
        });
        //console.log(`[obtenerUbicacion] ⚠️ No se encontraron ubicaciones válidas, usando 'Indeterminado'`);
      }

      // console.log(`[obtenerUbicacion] 🎯 ESTRATEGIA SEPARAR - ${ubicacionesEncontradas.length} ubicaciones:`,
      //   ubicacionesEncontradas.map(u => `${u.ubicacion} (${u.categoria})`).join(', '));

      // ✅ RETORNAR EL OBJETO CORRECTO
      return { ubicacionesMultiples: ubicacionesEncontradas };
    };



    // ✅ PROCESAR REGISTROS SEGÚN TIPO DE REPORTE
    /*if (filtros.tipo_reporte === 3) {
      // ✅ ENTRADA Y SALIDA - PARA CUALQUIER TIPO DE VISTA
      //console.log(`[${aliasVehiculo}] Modo: ENTRADA Y SALIDA - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];
        const { ubicacion, categoria } = obtenerUbicacion(registro);

        //console.log(`[${aliasVehiculo}] ${registro.fecha}: ${ubicacion} (${categoria})`);

        // ✅ NUEVA LÓGICA: Determinar qué ubicaciones son válidas según tipo_vista
        let esUbicacionValida = false;

        switch (filtros.tipo_vista) {
          case 0: // GEOCERCAS
            esUbicacionValida = ubicacion !== 'Indeterminado' &&
              ubicacion !== 'Traslado' &&
              categoria === 'Geocercas';
            break;

          case 1: // CLIENTES
            esUbicacionValida = ubicacion !== 'Indeterminado' &&
              ubicacion !== 'Traslado' &&
              categoria === 'Clientes';
            break;

          case 2: // REFERENCIAS
            esUbicacionValida = ubicacion !== 'Indeterminado' &&
              ubicacion !== 'Traslado' &&
              categoria === 'Referencias';
            break;

          case 3: // TODOS (COMBINADO)
            esUbicacionValida = ubicacion !== 'Indeterminado' &&
              ubicacion !== 'Traslado' &&
              (categoria === 'Geocercas' ||
                categoria === 'Clientes' ||
                categoria === 'Referencias');
            break;

          case -1: // INDETERMINADOS
            esUbicacionValida = ubicacion === 'Indeterminado' ||
              (ubicacion === 'Traslado' && categoria === 'Indeterminado');
            break;

          default: // FALLBACK - TODOS EXCEPTO TRASLADO
            esUbicacionValida = ubicacion !== 'Traslado';
            break;
        }

        //console.log(`[${aliasVehiculo}] Ubicación válida para tipo_vista ${filtros.tipo_vista}: ${esUbicacionValida}`);

        // ✅ CASO 1: ENTRADA A UBICACIÓN VÁLIDA
        if (esUbicacionValida && !estadiaInicio) {
          estadiaInicio = {
            registro,
            fechaInicio: convertirFechaLocal(registro.fecha),
            ubicacion,
            categoria
          };
          //console.log(`[${aliasVehiculo}] 🚪 ENTRADA A ${categoria.toUpperCase()}: ${ubicacion}`);
        }

        // ✅ CASO 2: SALIDA DE UBICACIÓN VÁLIDA
        else if (!esUbicacionValida && estadiaInicio) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🚪 SALIDA DE ${estadiaInicio.categoria.toUpperCase()}: ${estadiaInicio.ubicacion}, Duración: ${tiempoSegundos}s`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            const estadia = crearObjetoEstadia({
              vehiculoUid,
              registro: estadiaInicio.registro,
              fechaInicio: estadiaInicio.fechaInicio,
              fechaFin: fechaFin,
              ubicacionInicio: estadiaInicio.ubicacion,
              ubicacionFin: estadiaInicio.ubicacion, // Mismo lugar
              categoria: estadiaInicio.categoria,
              tiempoSegundos,
              tipoReporte: 'Entrada/Salida',
              todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
            });

            estadias.push(estadia);
            //console.log(`[${aliasVehiculo}] ✅ ESTADÍA CREADA: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
          } else {
            //console.log(`[${aliasVehiculo}] ❌ Estadía descartada por tiempo insuficiente: ${tiempoSegundos}s < ${tiempoMinimoEstadia}s`);
          }

          estadiaInicio = null;
        }

        // ✅ CASO 3: CAMBIO ENTRE UBICACIONES VÁLIDAS DEL MISMO TIPO
        else if (esUbicacionValida && estadiaInicio && ubicacion !== estadiaInicio.ubicacion) {
          // ✅ VERIFICAR SI ES CAMBIO DENTRO DEL MISMO TIPO DE VISTA
          let esCambioValidoDentroDelTipo = false;

          switch (filtros.tipo_vista) {
            case 0: // GEOCERCAS - Solo cambios entre geocercas
              esCambioValidoDentroDelTipo = categoria === 'Geocercas' && estadiaInicio.categoria === 'Geocercas';
              break;

            case 1: // CLIENTES - Solo cambios entre clientes
              esCambioValidoDentroDelTipo = categoria === 'Clientes' && estadiaInicio.categoria === 'Clientes';
              break;

            case 2: // REFERENCIAS - Solo cambios entre referencias
              esCambioValidoDentroDelTipo = categoria === 'Referencias' && estadiaInicio.categoria === 'Referencias';
              break;

            case 3: // TODOS - Cambios entre cualquier tipo válido
              esCambioValidoDentroDelTipo = true; // Cualquier cambio es válido en modo "TODOS"
              break;

            case -1: // INDETERMINADOS - Solo cambios entre indeterminados
              esCambioValidoDentroDelTipo = ubicacion === 'Indeterminado' && estadiaInicio.ubicacion === 'Indeterminado';
              break;

            default:
              esCambioValidoDentroDelTipo = true;
              break;
          }

          if (esCambioValidoDentroDelTipo) {
            // Finalizar estadía anterior
            const fechaFin = convertirFechaLocal(registro.fecha);
            const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

            //console.log(`[${aliasVehiculo}] 🔄 CAMBIO EN ${estadiaInicio.categoria.toUpperCase()}: ${estadiaInicio.ubicacion} → ${ubicacion}, Duración anterior: ${tiempoSegundos}s`);

            if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicio.registro,
                fechaInicio: estadiaInicio.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicio.ubicacion,
                ubicacionFin: estadiaInicio.ubicacion,
                categoria: estadiaInicio.categoria,
                tiempoSegundos,
                tipoReporte: 'Entrada/Salida'
              });

              estadias.push(estadia);
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA CREADA (cambio): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
            }

            // Iniciar nueva estadía en la nueva ubicación
            estadiaInicio = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria
            };
            //console.log(`[${aliasVehiculo}] 🚪 NUEVA ENTRADA EN ${categoria.toUpperCase()}: ${ubicacion}`);
          } else {
            //console.log(`[${aliasVehiculo}] ⚠️ Cambio de tipo no válido para tipo_vista ${filtros.tipo_vista}: ${estadiaInicio.categoria} → ${categoria}`);

            // Finalizar estadía actual y comenzar nueva
            const fechaFin = convertirFechaLocal(registro.fecha);
            const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

            if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicio.registro,
                fechaInicio: estadiaInicio.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicio.ubicacion,
                ubicacionFin: estadiaInicio.ubicacion,
                categoria: estadiaInicio.categoria,
                tiempoSegundos,
                tipoReporte: 'Entrada/Salida',
                todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
              });

              estadias.push(estadia);
            }

            // Iniciar nueva estadía si la nueva ubicación es válida para este tipo_vista
            estadiaInicio = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria
            };
          }
        }
      }

      // ✅ CERRAR ESTADÍA EN CURSO AL FINAL (si queda en ubicación válida)
      // if (estadiaInicio) {
      //   const ultimoRegistro = registros[registros.length - 1];
      //   const fechaFin = convertirFechaLocal(ultimoRegistro.fecha);
      //   const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

      //   console.log(`[${aliasVehiculo}] 📝 ESTADÍA EN CURSO AL FINAL: ${tiempoSegundos}s en ${estadiaInicio.categoria}`);

      //   if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
      //     const estadia = crearObjetoEstadia({
      //       vehiculoUid,
      //       registro: estadiaInicio.registro,
      //       fechaInicio: estadiaInicio.fechaInicio,
      //       fechaFin: fechaFin,
      //       ubicacionInicio: estadiaInicio.ubicacion,
      //       ubicacionFin: estadiaInicio.ubicacion,
      //       categoria: estadiaInicio.categoria,
      //       tiempoSegundos,
      //       tipoReporte: 'Entrada/Salida (En curso)'
      //     });

      //     estadias.push(estadia);
      //     console.log(`[${aliasVehiculo}] ✅ ESTADÍA EN CURSO: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
      //   }
      // }

    }*/ // ✅ CERRAR BLOQUE de tipo_reporte === 3
    // ✅ PROCESAR REGISTROS SEGÚN TIPO DE REPORTE CON ESTRATEGIA SEPARAR
    if (filtros.tipo_reporte === 3) {
      // ✅ ENTRADA Y SALIDA CON MÚLTIPLES UBICACIONES
      //console.log(`[${aliasVehiculo}] Modo: ENTRADA Y SALIDA (SEPARAR) - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];
        const { ubicacionesMultiples } = obtenerUbicacion(registro);

        // ✅ PROCESAR CADA UBICACIÓN POR SEPARADO
        ubicacionesMultiples.forEach(ubicacionData => {
          const { ubicacion, categoria } = ubicacionData;

          //console.log(`[${aliasVehiculo}] ${registro.fecha}: ${ubicacion} (${categoria})`);

          // ✅ VALIDAR SEGÚN TIPO DE VISTA
          let esUbicacionValida = false;

          switch (filtros.tipo_vista) {
            case 0: // GEOCERCAS
              esUbicacionValida = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Geocercas';
              break;

            case 1: // CLIENTES
              esUbicacionValida = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Clientes';
              break;

            case 2: // REFERENCIAS
              esUbicacionValida = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Referencias';
              break;

            case 3: // TODOS
              esUbicacionValida = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                (categoria === 'Geocercas' || categoria === 'Clientes' || categoria === 'Referencias');
              break;

            case -1: // INDETERMINADOS
              esUbicacionValida = ubicacion === 'Indeterminado';
              break;

            default:
              esUbicacionValida = ubicacion !== 'Traslado';
              break;
          }

          // ✅ BUSCAR ESTADÍA EXISTENTE PARA ESTA UBICACIÓN ESPECÍFICA
          let estadiaExistente = estadias.find(e =>
            e.uid_vehiculo === vehiculoUid &&
            e.punto_inicio === ubicacion &&
            e.estadiaActiva === true
          );

          // ✅ CASO 1: ENTRADA A UBICACIÓN VÁLIDA
          if (esUbicacionValida && !estadiaExistente) {
            // Crear nueva estadía para esta ubicación específica
            const nuevaEstadia = {
              vehiculoUid,
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria,
              estadiaActiva: true,
              uid_vehiculo: vehiculoUid,
              punto_inicio: ubicacion,
              tipo_ubicacion: categoria
            };

            estadias.push(nuevaEstadia);
            //console.log(`[${aliasVehiculo}] 🚪 ENTRADA SEPARADA A ${categoria.toUpperCase()}: ${ubicacion}`);
          }

          // ✅ CASO 2: CONFIRMAR ESTADÍA EXISTENTE (mismo punto)
          else if (esUbicacionValida && estadiaExistente && estadiaExistente.estadiaActiva) {
            // Actualizar tiempo de la estadía existente
            estadiaExistente.ultimaActualizacion = convertirFechaLocal(registro.fecha);
            //console.log(`[${aliasVehiculo}] 🔄 CONFIRMANDO estadía en ${categoria.toUpperCase()}: ${ubicacion}`);
          }
        });

        // ✅ VERIFICAR SALIDAS: Cerrar estadías que ya no aparecen en este registro
        const ubicacionesActuales = ubicacionesMultiples.map(u => u.ubicacion);

        // Buscar estadías activas que no están en las ubicaciones actuales
        const estadiasActivas = estadias.filter(e =>
          e.uid_vehiculo === vehiculoUid &&
          e.estadiaActiva === true
        );

        estadiasActivas.forEach(estadiaActiva => {
          const ubicacionEstadia = estadiaActiva.punto_inicio;

          // Si esta ubicación ya no está presente, es una SALIDA
          if (!ubicacionesActuales.includes(ubicacionEstadia)) {
            const fechaFin = convertirFechaLocal(registro.fecha);
            const tiempoSegundos = Math.floor((fechaFin - estadiaActiva.fechaInicio) / 1000);

            //console.log(`[${aliasVehiculo}] 🚪 SALIDA SEPARADA DE ${estadiaActiva.categoria?.toUpperCase()}: ${ubicacionEstadia}, Duración: ${tiempoSegundos}s`);

            if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
              // Finalizar esta estadía específica
              const estadiaFinalizada = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaActiva.registro,
                fechaInicio: estadiaActiva.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaActiva.ubicacion,
                ubicacionFin: estadiaActiva.ubicacion,
                //categoria: estadiaActiva.categoria,
                categoria: estadiaActiva.categoria || estadiaActiva.tipo_ubicacion || 'Indeterminado',
                tiempoSegundos,
                tipoReporte: 'Entrada/Salida (Separada)',
                todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
              });

              // ✅ LOG ESPECÍFICO PARA ENTRADA/SALIDA CON TIEMPOS DE MOTOR
              if (estadiaFinalizada.tiempo_apagado > 0 || estadiaFinalizada.tiempo_encendido > 0) {
                //console.log(`[${aliasVehiculo}] 📊 TIEMPOS MOTOR ENTRADA/SALIDA: Encendido ${formatearTiempo(estadiaFinalizada.tiempo_encendido || 0)}, Apagado ${formatearTiempo(estadiaFinalizada.tiempo_apagado || 0)}`);

                if (estadiaFinalizada.tiempo_apagado > 0) {
                  const porcentajeApagado = ((estadiaFinalizada.tiempo_apagado / tiempoSegundos) * 100).toFixed(1);
                  //console.log(`[${aliasVehiculo}] 🔍 ANÁLISIS MOTOR APAGADO E/S: ${formatearTiempo(estadiaFinalizada.tiempo_apagado)} (${porcentajeApagado}%) en ${ubicacionEstadia}`);
                }
              }

              // Remover de estadías activas
              const indiceActiva = estadias.findIndex(e => e === estadiaActiva);
              if (indiceActiva !== -1) {
                estadias[indiceActiva] = estadiaFinalizada; // Reemplazar con versión finalizada
              }

              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA SEPARADA CREADA: ${formatearTiempo(tiempoSegundos)} en ${ubicacionEstadia} (${estadiaActiva.categoria})`);
            } else {
              // Remover estadía que no cumple tiempo mínimo
              const indiceActiva = estadias.findIndex(e => e === estadiaActiva);
              if (indiceActiva !== -1) {
                estadias.splice(indiceActiva, 1);
              }
              //console.log(`[${aliasVehiculo}] ❌ Estadía separada descartada por tiempo insuficiente: ${tiempoSegundos}s < ${tiempoMinimoEstadia}s`);
            }
          }
        });
      }

      // ✅ LIMPIAR ESTADÍAS ACTIVAS AL FINAL (las que quedaron sin cerrar)
      const estadiasActivasFinales = estadias.filter(e =>
        e.uid_vehiculo === vehiculoUid &&
        e.estadiaActiva === true
      );

      estadiasActivasFinales.forEach(estadiaActiva => {
        //console.warn(`[${aliasVehiculo}] 🚫 ESTADÍA SEPARADA INCOMPLETA - REMOVIDA: ${estadiaActiva.ubicacion} (${estadiaActiva.categoria})`);

        const indiceActiva = estadias.findIndex(e => e === estadiaActiva);
        if (indiceActiva !== -1) {
          estadias.splice(indiceActiva, 1);
        }
      });

    }
    else if (filtros.tipo_reporte === 1) {
      // ✅ APAGADO/ENCENDIDO CON ESTRATEGIA SEPARAR
      //console.log(`[${aliasVehiculo}] Modo: APAGADO/ENCENDIDO (SEPARAR) - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE APAGADO/ENCENDIDO - MAPEO ESTRICTO POR TIPO
        const esApagado = registro.tipo === 3 || registro.tipo === 5;
        const esEncendido = registro.tipo === 4 || registro.tipo === 6;

        // ✅ INICIO DE ESTADÍA (APAGADO) - CORREGIR USO DE obtenerUbicacion
        if (esApagado && !estadiaInicio) {
          // ✅ USAR LA FUNCIÓN CORRECTA
          const resultadoUbicacion = obtenerUbicacion(registro);

          if (!resultadoUbicacion || !resultadoUbicacion.ubicacionesMultiples) {
            //console.warn(`[${aliasVehiculo}] ❌ Error: obtenerUbicacion no retornó ubicacionesMultiples`);
            continue;
          }

          const { ubicacionesMultiples } = resultadoUbicacion;

          // ✅ PROCESAR LA PRIMERA UBICACIÓN VÁLIDA (simplificado para A/E)
          const ubicacionPrincipal = ubicacionesMultiples[0];

          if (ubicacionPrincipal) {
            estadiaInicio = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion: ubicacionPrincipal.ubicacion,
              categoria: ubicacionPrincipal.categoria,
              ubicacionesMultiples // ✅ Guardar todas para referencia
            };

            //console.log(`[${aliasVehiculo}] 🔴 APAGADO DETECTADO en ${ubicacionPrincipal.categoria}: ${ubicacionPrincipal.ubicacion}`);
          }
        }

        else if (esEncendido && estadiaInicio) {
          //console.log(`[${aliasVehiculo}] 🟢 ENCENDIDO DETECTADO (A/E): ${registro.nombreEvento || 'evento sin nombre'} (tipo: ${registro.tipo}, tipoMotor: ${registro.tipoMotor}) - fecha=${registro.fecha}`);
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            // ✅ APLICAR FILTRO DE TIPO_VISTA AQUÍ
            let incluirEstadia = false;

            switch (filtros.tipo_vista) {
              case 0: // GEOCERCAS
                incluirEstadia = estadiaInicio.categoria === 'Geocercas';
                break;
              case 1: // CLIENTES
                incluirEstadia = estadiaInicio.categoria === 'Clientes';
                break;
              case 2: // REFERENCIAS
                incluirEstadia = estadiaInicio.categoria === 'Referencias';
                break;
              case 3: // TODOS
                incluirEstadia = true; // Incluir todas
                break;
              case -1: // INDETERMINADOS
                incluirEstadia = estadiaInicio.ubicacion === 'Indeterminado';
                break;
              default:
                incluirEstadia = true;
                break;
            }

            if (incluirEstadia) {
              // ✅ CREAR ESTADÍA CON VALIDACIÓN SEGURA
              try {
                const estadia = crearObjetoEstadia({
                  vehiculoUid,
                  registro: estadiaInicio.registro,
                  fechaInicio: (estadiaInicio.fechaInicio),
                  fechaFin: (fechaFin),
                  ubicacionInicio: estadiaInicio.ubicacion,
                  ubicacionFin: estadiaInicio.ubicacion,
                  categoria: estadiaInicio.categoria,
                  tiempoSegundos,
                  tipoReporte: 'A/E',
                  todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
                });

                estadias.push(estadia);
                //console.log(`[${aliasVehiculo}] ✅ ESTADÍA A/E CREADA: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);

              } catch (error) {
                console.error(`[${aliasVehiculo}] ❌ Error creando estadía A/E:`, error);
              }
            }
          }

          // ✅ REINICIAR SIEMPRE
          estadiaInicio = null;
        }

        // ✅ CASOS ESPECIALES - LOGS SIMPLIFICADOS
        else if (esApagado && estadiaInicio) {
          // Apagado detectado pero ya hay estadía activa - ignorar
        }
        else if (esEncendido && !estadiaInicio) {
          //console.warn(`[${aliasVehiculo}] ⚠️ ENCENDIDO SIN APAGADO PREVIO (A/E): ${registro.nombreEvento || 'evento sin nombre'} (tipo: ${registro.tipo}, tipoMotor: ${registro.tipoMotor}) - fecha=${registro.fecha}`);
          // Encendido detectado pero no hay estadía activa - ignorar
        }
      }

      // ✅ VERIFICAR ESTADÍA SIN CERRAR AL FINAL
      if (estadiaInicio) {
        //console.warn(`[${aliasVehiculo}] 🚫 ESTADÍA A/E SIN CERRAR AL FINAL:`, {
        //  ubicacion: estadiaInicio.ubicacion,
        //  categoria: estadiaInicio.categoria,
        //  fechaInicio: estadiaInicio.fechaInicio,
        //  duracionSinCerrar: Math.floor((new Date() - estadiaInicio.fechaInicio) / 1000)
        //});

        // ✅ OPCIONAL: Cerrar con el último registro si quieres
        // const ultimoRegistro = registros[registros.length - 1];
        // const fechaFin = convertirFechaLocal(ultimoRegistro.fecha);
        // ... crear estadía ...
      }

      //console.log(`[${aliasVehiculo}] 📊 RESUMEN A/E: ${estadias.filter(e => e.uid_vehiculo === vehiculoUid && e.tipo_reporte === 'A/E').length} estadías creadas`);

    }
    /*else if (filtros.tipo_reporte === 1) {
      // ✅ APAGADO/ENCENDIDO - NUEVA LÓGICA ESPECÍFICA
      //console.log(`[${aliasVehiculo}] Modo: APAGADO/ENCENDIDO - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE APAGADO/ENCENDIDO MÁS AMPLIAMENTE
        const esApagado = registro.tipoMotor === 0 ||
          registro.tipo === 5 ||
          registro.tipo === 3 ||
          registro.nombreEvento?.toLowerCase().includes('apagado') ||
          registro.nombreEvento?.toLowerCase().includes('off');

        const esEncendido = registro.tipoMotor === 1 ||
          registro.tipo === 6 ||
          registro.tipo === 4 ||
          registro.nombreEvento?.toLowerCase().includes('encendido') ||
          registro.nombreEvento?.toLowerCase().includes('on');

        //console.log(`[${aliasVehiculo}] ${registro.fecha}: Evento=${registro.nombreEvento}, Tipo=${registro.tipo}, TipoMotor=${registro.tipoMotor}, EsApagado=${esApagado}, EsEncendido=${esEncendido}`);

        // ✅ INICIO DE ESTADÍA (APAGADO) - SIN FILTROS DE TIPO_VISTA
        if (esApagado && !esEncendido && !estadiaInicio) {
          const { ubicacion, categoria } = obtenerUbicacion(registro);

          estadiaInicio = {
            registro,
            fechaInicio: convertirFechaLocal(registro.fecha),
            ubicacion,
            categoria
          };
          //console.log(`[${aliasVehiculo}] 🔴 APAGADO en ${categoria}: ${ubicacion}`);
        }

        // ✅ FIN DE ESTADÍA (ENCENDIDO) - SIN FILTROS DE TIPO_VISTA
        else if (esEncendido && !esApagado && estadiaInicio) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🟢 ENCENDIDO - Duración: ${tiempoSegundos}s`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            // ✅ APLICAR FILTRO DE TIPO_VISTA AQUÍ (AL FINAL)
            let incluirEstadia = false;

            switch (filtros.tipo_vista) {
              case 0: // GEOCERCAS
                incluirEstadia = estadiaInicio.categoria === 'Geocercas';
                break;
              case 1: // CLIENTES
                incluirEstadia = estadiaInicio.categoria === 'Clientes';
                break;
              case 2: // REFERENCIAS
                incluirEstadia = estadiaInicio.categoria === 'Referencias';
                break;
              case 3: // TODOS
                incluirEstadia = true; // Incluir todas
                break;
              case -1: // INDETERMINADOS
                incluirEstadia = estadiaInicio.ubicacion === 'Indeterminado';
                break;
              default:
                incluirEstadia = true;
                break;
            }

            if (incluirEstadia) {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicio.registro,
                fechaInicio: estadiaInicio.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicio.ubicacion,
                ubicacionFin: estadiaInicio.ubicacion,
                categoria: estadiaInicio.categoria,
                tiempoSegundos,
                tipoReporte: 'A/E'
              });

              estadias.push(estadia);
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA A/E: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
            } else {
              //console.log(`[${aliasVehiculo}] ❌ Estadía A/E filtrada por tipo_vista ${filtros.tipo_vista}: ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
            }
          } else {
            // console.log(`[${aliasVehiculo}] ❌ Estadía A/E descartada por tiempo: ${tiempoSegundos}s < ${tiempoMinimoEstadia}s`);
          }

          estadiaInicio = null;
        }
      }     

    }*/
    // ✅ CERRAR BLOQUE de tipo_reporte === 1
    else if (filtros.tipo_reporte === 0) {
      // ✅ POR DETENIDOS CON ESTRATEGIA SEPARAR
      //console.log(`[${aliasVehiculo}] Modo: POR DETENIDOS (SEPARAR) - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE DETENIDO/MOVIMIENTO POR TIPOS ESPECÍFICOS
        const esDetenido = registro.tipo === 30 ||
          registro.tipo === 11 ||
          registro.tipo === 12 ||
          registro.tipo === 17 ||
          registro.tipo === 18 ||
          (registro.nombreEvento && registro.nombreEvento.toLowerCase().includes('detenido'));

        // ✅ CUALQUIER EVENTO QUE NO SEA DETENIDO DEBE CERRAR LA ESTADÍA
        const esEventoQueRompeDetenido = !esDetenido; // Cualquier cosa que no sea "Detenido"

        // ✅ INICIO DE ESTADÍA (DETENIDO) - USAR NUEVA FUNCIÓN
        if (esDetenido && !estadiaInicio) {
          const resultadoUbicacion = obtenerUbicacion(registro);

          if (!resultadoUbicacion || !resultadoUbicacion.ubicacionesMultiples) {
            //console.warn(`[${aliasVehiculo}] ❌ Error: obtenerUbicacion no retornó ubicacionesMultiples`);
            continue;
          }

          const { ubicacionesMultiples } = resultadoUbicacion;
          const ubicacionPrincipal = ubicacionesMultiples[0];

          if (ubicacionPrincipal) {
            const { ubicacion, categoria } = ubicacionPrincipal;

            // ✅ VERIFICAR SI ES VÁLIDA SEGÚN TIPO DE VISTA
            let esUbicacionValidaParaDetenidos = false;

            switch (filtros.tipo_vista) {
              case 0: // GEOCERCAS
                esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Geocercas';
                break;
              case 1: // CLIENTES
                esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Clientes';
                break;
              case 2: // REFERENCIAS
                esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Referencias';
                break;
              case 3: // TODOS
                esUbicacionValidaParaDetenidos = true;
                break;
              case -1: // INDETERMINADOS
                esUbicacionValidaParaDetenidos = ubicacion === 'Indeterminado';
                break;
              default:
                esUbicacionValidaParaDetenidos = true;
                break;
            }

            if (esUbicacionValidaParaDetenidos) {
              // 🔥 DETECCIÓN ESPECIAL: DETENIDO CON MOTOR APAGADO
              const motorApagado = registro.tipoMotor === 0 ||
                registro.tipo === 5 ||
                (registro.nombreEvento && registro.nombreEvento.toLowerCase().includes('apagado'));

              if (motorApagado) {
                //console.log(`[${aliasVehiculo}] CASO ESPECIAL: DETENIDO CON MOTOR APAGADO detectado en ${categoria}: ${ubicacion}`, {
                //  tipoMotor: registro.tipoMotor,
                //  tipoEvento: registro.tipo,
                //  nombreEvento: registro.nombreEvento,
                //  fecha: registro.fecha
                //});
              }

              estadiaInicio = {
                registro,
                fechaInicio: convertirFechaLocal(registro.fecha),
                ubicacion,
                categoria,
                ubicacionesMultiples,
                motorApagadoDetectado: motorApagado // ✅ Marcar si se detectó motor apagado
              };

              //console.log(`[${aliasVehiculo}] 🛑 DETENIDO DETECTADO en ${categoria}: ${ubicacion}${motorApagado ? ' (MOTOR APAGADO)' : ''}`);
            }
          }
        }

        // ✅ FIN DE ESTADÍA (CUALQUIER EVENTO QUE NO SEA DETENIDO)
        else if (esEventoQueRompeDetenido && estadiaInicio) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🛑 FIN DE ESTADÍA DETENIDO por evento "${registro.nombreEvento}" (tipo: ${registro.tipo}) - Duración: ${tiempoSegundos}s`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            // ✅ CREAR ESTADÍA DE DETENIDO
            try {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicio.registro,
                fechaInicio: estadiaInicio.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicio.ubicacion,
                ubicacionFin: estadiaInicio.ubicacion,
                categoria: estadiaInicio.categoria,
                tiempoSegundos,
                tipoReporte: 'Detenidos',
                todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
              });

              estadias.push(estadia);

              // 🔥 LOG ESPECIAL SI HABÍA MOTOR APAGADO DETECTADO
              const tipoEspecial = estadiaInicio.motorApagadoDetectado ? ' (CON MOTOR APAGADO)' : '';
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO CREADA${tipoEspecial}: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria}) - Cerrada por: ${registro.nombreEvento}`);

              // ✅ LOG CON TIEMPOS DE MOTOR SOLO SI ES CASO ESPECIAL O HAY DIFERENCIAS SIGNIFICATIVAS
              if (estadiaInicio.motorApagadoDetectado || (estadia.tiempo_encendido > 0 && estadia.tiempo_apagado > 0)) {
                //console.log(`[${aliasVehiculo}] 📊 Tiempos de motor - Encendido: ${formatearTiempo(estadia.tiempo_encendido || 0)}, Apagado: ${formatearTiempo(estadia.tiempo_apagado || 0)}`);

                // ✅ LOG DETALLADO PARA ENTENDER DE DÓNDE VIENE EL TIEMPO DE MOTOR APAGADO
                if (estadia.tiempo_apagado > 0) {
                  //console.log(`[${aliasVehiculo}] 🔍 ANÁLISIS MOTOR APAGADO EN ESTADÍA DETENIDO:`, {
                  //   registroInicio: {
                  //     fecha: estadiaInicio.registro.fecha,
                  //     tipo: estadiaInicio.registro.tipo,
                  //     tipoMotor: estadiaInicio.registro.tipoMotor,
                  //     nombreEvento: estadiaInicio.registro.nombreEvento
                  //   },
                  //   registroFin: {
                  //     fecha: registro.fecha,
                  //     tipo: registro.tipo,
                  //     tipoMotor: registro.tipoMotor,
                  //     nombreEvento: registro.nombreEvento
                  //   },
                  //   duracionTotal: `${formatearTiempo(tiempoSegundos)}`,
                  //   tiempoApagadoCalculado: `${formatearTiempo(estadia.tiempo_apagado)}`,
                  //   porcentajeApagado: `${((estadia.tiempo_apagado / tiempoSegundos) * 100).toFixed(1)}%`
                  // });
                }
              }

            } catch (error) {
              console.error(`[${aliasVehiculo}] ❌ Error creando estadía DETENIDO:`, error);
            }
          }

          // ✅ REINICIAR ESTADÍA SIEMPRE DESPUÉS DE CUALQUIER EVENTO QUE NO SEA DETENIDO
          estadiaInicio = null;
        }

        // ✅ CAMBIO DE UBICACIÓN MIENTRAS ESTÁ DETENIDO
        else if (esDetenido && estadiaInicio) {
          const resultadoUbicacion = obtenerUbicacion(registro);

          if (!resultadoUbicacion || !resultadoUbicacion.ubicacionesMultiples) {
            continue;
          }

          const { ubicacionesMultiples } = resultadoUbicacion;
          const ubicacionPrincipal = ubicacionesMultiples[0];

          if (ubicacionPrincipal) {
            const { ubicacion, categoria } = ubicacionPrincipal;

            // Si cambió de ubicación, cerrar estadía anterior y abrir nueva
            if (ubicacion !== estadiaInicio.ubicacion) {
              const fechaFin = convertirFechaLocal(registro.fecha);
              const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

              // Cerrar estadía anterior si cumple tiempo mínimo
              if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
                try {
                  const estadia = crearObjetoEstadia({
                    vehiculoUid,
                    registro: estadiaInicio.registro,
                    fechaInicio: estadiaInicio.fechaInicio,
                    fechaFin: fechaFin,
                    ubicacionInicio: estadiaInicio.ubicacion,
                    ubicacionFin: estadiaInicio.ubicacion,
                    categoria: estadiaInicio.categoria,
                    tiempoSegundos,
                    tipoReporte: 'Detenidos',
                    todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
                  });

                  estadias.push(estadia);

                  const tipoEspecial = estadiaInicio.motorApagadoDetectado ? ' (CON MOTOR APAGADO)' : '';
                  //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO (cambio ubicación)${tipoEspecial}: ${formatearTiempo(tiempoSegundos)} - ${estadiaInicio.ubicacion} → ${ubicacion}`);

                  // Log de tiempos solo si es especial
                  if (estadiaInicio.motorApagadoDetectado || (estadia.tiempo_encendido > 0 && estadia.tiempo_apagado > 0)) {
                    //console.log(`[${aliasVehiculo}] 📊 Tiempos: Encendido ${formatearTiempo(estadia.tiempo_encendido || 0)}, Apagado ${formatearTiempo(estadia.tiempo_apagado || 0)}`);

                    // ✅ LOG DETALLADO PARA CAMBIO DE UBICACIÓN CON MOTOR APAGADO
                    if (estadia.tiempo_apagado > 0) {
                      //console.log(`[${aliasVehiculo}] 🔍 ANÁLISIS MOTOR APAGADO (cambio ubicación):`, {
                      //   registroInicio: {
                      //     fecha: estadiaInicio.registro.fecha,
                      //     tipo: estadiaInicio.registro.tipo,
                      //     tipoMotor: estadiaInicio.registro.tipoMotor,
                      //     nombreEvento: estadiaInicio.registro.nombreEvento
                      //   },
                      //   duracionTotal: `${formatearTiempo(tiempoSegundos)}`,
                      //   tiempoApagadoCalculado: `${formatearTiempo(estadia.tiempo_apagado)}`,
                      //   porcentajeApagado: `${((estadia.tiempo_apagado / tiempoSegundos) * 100).toFixed(1)}%`
                      // });
                    }
                  }
                } catch (error) {
                  console.error(`[${aliasVehiculo}] ❌ Error creando estadía DETENIDO (cambio):`, error);
                }
              }

              // ✅ VERIFICAR SI NUEVA UBICACIÓN ES VÁLIDA - SIMPLIFICADO
              let esNuevaUbicacionValida = (filtros.tipo_vista === 3) ? true :
                (filtros.tipo_vista === 0 && categoria === 'Geocercas') ||
                (filtros.tipo_vista === 1 && categoria === 'Clientes') ||
                (filtros.tipo_vista === 2 && categoria === 'Referencias') ||
                (filtros.tipo_vista === -1 && ubicacion === 'Indeterminado');

              // Iniciar nueva estadía si la nueva ubicación es válida
              if (esNuevaUbicacionValida) {
                // Detectar motor apagado en nueva ubicación
                const motorApagadoNuevo = registro.tipoMotor === 0 ||
                  registro.tipo === 5 ||
                  (registro.nombreEvento && registro.nombreEvento.toLowerCase().includes('apagado'));

                if (motorApagadoNuevo) {
                  //console.log(`[${aliasVehiculo}] 🚨 NUEVA UBICACIÓN CON MOTOR APAGADO: ${ubicacion} (${categoria})`);
                }

                estadiaInicio = {
                  registro,
                  fechaInicio: convertirFechaLocal(registro.fecha),
                  ubicacion,
                  categoria,
                  ubicacionesMultiples,
                  motorApagadoDetectado: motorApagadoNuevo
                };
              } else {
                estadiaInicio = null;
              }
            }
          }
        }
      }

      // ✅ VERIFICAR ESTADÍA SIN CERRAR AL FINAL
      if (estadiaInicio) {
        //console.warn(`[${aliasVehiculo}] 🚫 ESTADÍA DETENIDO INCOMPLETA - NO AGREGADA:`, {
        //   ubicacion: estadiaInicio.ubicacion,
        //   categoria: estadiaInicio.categoria,
        //   fechaInicio: estadiaInicio.fechaInicio,
        //   razon: 'Sin evento que rompa el ciclo de detenido para cerrar'
        // });
      }

      //console.log(`[${aliasVehiculo}] 📊 RESUMEN DETENIDOS: ${estadias.filter(e => e.uid_vehiculo === vehiculoUid && e.tipo_reporte === 'Detenidos').length} estadías creadas`);

    }

    /*else if (filtros.tipo_reporte === 0) {
      // ✅ POR DETENIDOS - CORREGIDO: Usar tipos específicos de eventos
      //console.log(`[${aliasVehiculo}] Modo: POR DETENIDOS - Tipo vista: ${filtros.tipo_vista}`);

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE DETENIDO/MOVIMIENTO POR TIPOS ESPECÍFICOS
        const esDetenido = registro.tipo === 30 ||
          registro.tipo === 11 ||
          registro.tipo === 12 ||
          registro.tipo === 17 ||
          registro.tipo === 18 ||
          registro.nombreEvento?.toLowerCase().includes('detenido');

        const esMovimiento = registro.nombreEvento?.toLowerCase().includes('movimiento') ||
          registro.tipo === 1 ||  // Tipo movimiento general
          registro.tipo === 2;    // Otro tipo de movimiento

        //console.log(`[${aliasVehiculo}] ${registro.fecha}: Evento=${registro.nombreEvento}, Tipo=${registro.tipo}, EsDetenido=${esDetenido}, EsMovimiento=${esMovimiento}`);

        // ✅ INICIO DE ESTADÍA (DETENIDO)
        if (esDetenido && !estadiaInicio) {
          const { ubicacion, categoria } = obtenerUbicacion(registro);

          // ✅ VERIFICAR SI ES VÁLIDA SEGÚN TIPO DE VISTA
          let esUbicacionValidaParaDetenidos = false;

          switch (filtros.tipo_vista) {
            case 0: // GEOCERCAS
              esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Geocercas';
              break;

            case 1: // CLIENTES
              esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Clientes';
              break;

            case 2: // REFERENCIAS
              esUbicacionValidaParaDetenidos = ubicacion !== 'Indeterminado' &&
                ubicacion !== 'Traslado' &&
                categoria === 'Referencias';
              break;

            case 3: // TODOS
              esUbicacionValidaParaDetenidos = true; // Incluir todas las ubicaciones
              break;

            case -1: // INDETERMINADOS
              esUbicacionValidaParaDetenidos = ubicacion === 'Indeterminado';
              break;

            default:
              esUbicacionValidaParaDetenidos = true;
              break;
          }

          if (esUbicacionValidaParaDetenidos) {
            estadiaInicio = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria
            };
            //console.log(`[${aliasVehiculo}] 🛑 DETENIDO (tipo ${registro.tipo}) en ${categoria}: ${ubicacion}`);
          } else {
            //console.log(`[${aliasVehiculo}] 🛑 DETENIDO ignorado (no válido para tipo_vista ${filtros.tipo_vista}): ${ubicacion} (${categoria})`);
          }
        }

        // ✅ FIN DE ESTADÍA (MOVIMIENTO)
        else if (esMovimiento && estadiaInicio) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🚗 MOVIMIENTO (tipo ${registro.tipo}) - Duración estadía: ${tiempoSegundos}s`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            // ✅ CREAR ESTADÍA DE DETENIDO
            const estadia = crearObjetoEstadia({
              vehiculoUid,
              registro: estadiaInicio.registro,
              fechaInicio: estadiaInicio.fechaInicio,
              fechaFin: fechaFin,
              ubicacionInicio: estadiaInicio.ubicacion,
              ubicacionFin: estadiaInicio.ubicacion,
              categoria: estadiaInicio.categoria,
              tiempoSegundos,
              tipoReporte: 'Detenidos'
            });

            estadias.push(estadia);
            //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO: ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion} (${estadiaInicio.categoria})`);
          } else {
            //console.log(`[${aliasVehiculo}] ❌ Estadía DETENIDO descartada por tiempo: ${tiempoSegundos}s < ${tiempoMinimoEstadia}s`);
          }

          estadiaInicio = null;
        }

        // ✅ CAMBIO DE UBICACIÓN MIENTRAS ESTÁ DETENIDO
        else if (esDetenido && estadiaInicio) {
          const { ubicacion, categoria } = obtenerUbicacion(registro);

          // Si cambió de ubicación, cerrar estadía anterior y abrir nueva
          if (ubicacion !== estadiaInicio.ubicacion) {
            const fechaFin = convertirFechaLocal(registro.fecha);
            const tiempoSegundos = Math.floor((fechaFin - estadiaInicio.fechaInicio) / 1000);

            //console.log(`[${aliasVehiculo}] 🔄 CAMBIO UBICACIÓN DETENIDO: ${estadiaInicio.ubicacion} → ${ubicacion}`);

            // Cerrar estadía anterior si cumple tiempo mínimo
            if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicio.registro,
                fechaInicio: estadiaInicio.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicio.ubicacion,
                ubicacionFin: estadiaInicio.ubicacion,
                categoria: estadiaInicio.categoria,
                tiempoSegundos,
                tipoReporte: 'Detenidos'
              });

              estadias.push(estadia);
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO (cambio ubicación): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicio.ubicacion}`);
            }

            // ✅ VERIFICAR SI NUEVA UBICACIÓN ES VÁLIDA PARA EL TIPO DE VISTA
            let esNuevaUbicacionValida = false;

            switch (filtros.tipo_vista) {
              case 0: // GEOCERCAS
                esNuevaUbicacionValida = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Geocercas';
                break;

              case 1: // CLIENTES
                esNuevaUbicacionValida = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Clientes';
                break;

              case 2: // REFERENCIAS
                esNuevaUbicacionValida = ubicacion !== 'Indeterminado' &&
                  ubicacion !== 'Traslado' &&
                  categoria === 'Referencias';
                break;

              case 3: // TODOS
                esNuevaUbicacionValida = true;
                break;

              case -1: // INDETERMINADOS
                esNuevaUbicacionValida = ubicacion === 'Indeterminado';
                break;

              default:
                esNuevaUbicacionValida = true;
                break;
            }

            // Iniciar nueva estadía si la nueva ubicación es válida
            if (esNuevaUbicacionValida) {
              estadiaInicio = {
                registro,
                fechaInicio: convertirFechaLocal(registro.fecha),
                ubicacion,
                categoria
              };
              //console.log(`[${aliasVehiculo}] 🛑 NUEVA ESTADÍA DETENIDO (tipo ${registro.tipo}) en ${categoria}: ${ubicacion}`);
            } else {
              estadiaInicio = null;
              //console.log(`[${aliasVehiculo}] 🛑 Nueva ubicación no válida para tipo_vista ${filtros.tipo_vista}: ${ubicacion} (${categoria})`);
            }
          }
        }
      }

      // ✅ NO CERRAR ESTADÍAS AUTOMÁTICAMENTE AL FINAL
      // Solo registrar en log que quedó una estadía sin cerrar
      if (estadiaInicio) {
        //console.warn(`[${aliasVehiculo}] 🚫 ESTADÍA DETENIDO INCOMPLETA - NO AGREGADA:`, {
          ubicacion: estadiaInicio.ubicacion,
          categoria: estadiaInicio.categoria,
          fechaInicio: estadiaInicio.fechaInicio,
          razon: 'Sin evento de movimiento para cerrar'
        });
      }

    }*/
    else if (filtros.tipo_reporte === 2) {
      // ✅ COMPLETO (DETENIDO Y E/A) CON ESTRATEGIA SEPARAR
      //console.log(`[${aliasVehiculo}] Modo: COMPLETO (Detenido + E/A - SEPARAR) - Tipo vista: ${filtros.tipo_vista}`);

      // 🔄 EJECUTAR LÓGICA DE DETENIDOS POR SEPARADO
  //console.log(`[${aliasVehiculo}] 📍 Ejecutando lógica de DETENIDOS... registros=${registros.length}`);
      let estadiaInicioDetenido = null;

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE DETENIDO POR TIPOS ESPECÍFICOS
        const esDetenido = registro.tipo === 30 ||
          registro.tipo === 11 ||
          registro.tipo === 12 ||
          registro.tipo === 17 ||
          registro.tipo === 18 ||
          (registro.nombreEvento && registro.nombreEvento.toLowerCase().includes('detenido'));

        const esMovimiento = (registro.nombreEvento && registro.nombreEvento.toLowerCase().includes('movimiento')) ||
          registro.tipo === 1 ||
          registro.tipo === 2;

        // INICIO DE ESTADÍA DETENIDO
        if (esDetenido && !estadiaInicioDetenido) {
          const resultadoUbicacion = obtenerUbicacion(registro);

          if (!resultadoUbicacion || !resultadoUbicacion.ubicacionesMultiples) {
            //console.warn(`[${aliasVehiculo}] ❌ Error DETENIDO (COMPLETO): obtenerUbicacion no retornó ubicacionesMultiples`);
            continue;
          }

          const { ubicacionesMultiples } = resultadoUbicacion;
          const ubicacionPrincipal = ubicacionesMultiples[0];

          if (ubicacionPrincipal) {
            const { ubicacion, categoria } = ubicacionPrincipal;

            if (cumpleFiltroPorTipoVista(ubicacion, categoria, filtros.tipo_vista)) {
              estadiaInicioDetenido = {
                registro,
                fechaInicio: convertirFechaLocal(registro.fecha),
                ubicacion,
                categoria,
                ubicacionesMultiples
              };
              //console.log(`[${aliasVehiculo}] 🛑 DETENIDO (COMPLETO): ${ubicacion} (${categoria})`);
              //console.log(`[${aliasVehiculo}] 📍 Total ubicaciones DETENIDO: ${ubicacionesMultiples.length}`);
            }
          }
        }

        // FIN DE ESTADÍA DETENIDO
        else if (esMovimiento && estadiaInicioDetenido) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicioDetenido.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🚗 MOVIMIENTO (COMPLETO) - Duración DETENIDO: ${tiempoSegundos}s`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            try {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicioDetenido.registro,
                fechaInicio: estadiaInicioDetenido.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicioDetenido.ubicacion,
                ubicacionFin: estadiaInicioDetenido.ubicacion,
                categoria: estadiaInicioDetenido.categoria,
                tiempoSegundos,
                tipoReporte: 'Detenidos', // ✅ Usar 'Detenidos' para aplicar lógica especial de motor
                todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS PARA CÁLCULO DE MOTOR
              });

              estadias.push(estadia);
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO (COMPLETO con lógica Detenidos): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicioDetenido.ubicacion} (${estadiaInicioDetenido.categoria})`);
            } catch (error) {
              console.error(`[${aliasVehiculo}] ❌ Error creando estadía DETENIDO (COMPLETO):`, error);
            }
          }

          estadiaInicioDetenido = null;
        }
      }

      // 🔄 EJECUTAR LÓGICA DE APAGADO/ENCENDIDO POR SEPARADO
  //console.log(`[${aliasVehiculo}] 🔴 Ejecutando lógica de APAGADO/ENCENDIDO... registros=${registros.length}`);
      let estadiaInicioAE = null;

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE APAGADO/ENCENDIDO POR TIPOS ESPECÍFICOS (MAPEO ESTRICTO)
        const esApagado = registro.tipo === 3 || registro.tipo === 5;
        const esEncendido = registro.tipo === 4 || registro.tipo === 6;

        // INICIO DE ESTADÍA APAGADO
        if (esApagado && !estadiaInicioAE) {
          const resultadoUbicacion = obtenerUbicacion(registro);

          if (!resultadoUbicacion || !resultadoUbicacion.ubicacionesMultiples) {
            //console.warn(`[${aliasVehiculo}] ❌ Error APAGADO (COMPLETO): obtenerUbicacion no retornó ubicacionesMultiples`);
            continue;
          }

          const { ubicacionesMultiples } = resultadoUbicacion;
          const ubicacionPrincipal = ubicacionesMultiples[0];

          if (ubicacionPrincipal) {
            const { ubicacion, categoria } = ubicacionPrincipal;

            if (cumpleFiltroPorTipoVista(ubicacion, categoria, filtros.tipo_vista)) {
              estadiaInicioAE = {
                registro,
                fechaInicio: convertirFechaLocal(registro.fecha),
                ubicacion,
                categoria,
                ubicacionesMultiples
              };
              //console.log(`[${aliasVehiculo}] 🔴 APAGADO (COMPLETO): ${ubicacion} (${categoria}) - fecha=${registro.fecha}`);
              //console.log(`[${aliasVehiculo}] 📍 Total ubicaciones APAGADO: ${ubicacionesMultiples.length}`);
            }
          }
        }

        // FIN DE ESTADÍA APAGADO
        else if (esEncendido && estadiaInicioAE) {
          //console.log(`[${aliasVehiculo}] 🟢 ENCENDIDO DETECTADO (COMPLETO A/E): ${registro.nombreEvento || 'evento sin nombre'} (tipo: ${registro.tipo}, tipoMotor: ${registro.tipoMotor}) - fecha=${registro.fecha}`);
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicioAE.fechaInicio) / 1000);

          //console.log(`[${aliasVehiculo}] 🟢 ENCENDIDO (COMPLETO) - Duración APAGADO: ${tiempoSegundos}s (mínimo=${tiempoMinimoEstadia}s) - fecha=${registro.fecha}`);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            try {
              const estadia = crearObjetoEstadia({
                vehiculoUid,
                registro: estadiaInicioAE.registro,
                fechaInicio: estadiaInicioAE.fechaInicio,
                fechaFin: fechaFin,
                ubicacionInicio: estadiaInicioAE.ubicacion,
                ubicacionFin: estadiaInicioAE.ubicacion,
                categoria: estadiaInicioAE.categoria,
                tiempoSegundos,
                tipoReporte: 'A/E', // ✅ Usar 'A/E' para aplicar lógica estándar de motor
                todosLosRegistros: registros // ✅ AGREGAR TODOS LOS REGISTROS
              });

              estadias.push(estadia);
              //console.log(`[${aliasVehiculo}] ✅ ESTADÍA APAGADO (COMPLETO con lógica A/E): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicioAE.ubicacion} (${estadiaInicioAE.categoria})`);
            } catch (error) {
              console.error(`[${aliasVehiculo}] ❌ Error creando estadía APAGADO (COMPLETO):`, error);
            }
          } else {
            //console.warn(`[${aliasVehiculo}] ⚠️ ENCENDIDO descartado por duración insuficiente: ${tiempoSegundos}s < ${tiempoMinimoEstadia}s en ${estadiaInicioAE.ubicacion}`);
          }

          estadiaInicioAE = null;
        } else if (esEncendido && !estadiaInicioAE) {
          //console.warn(`[${aliasVehiculo}] ⚠️ ENCENDIDO sin apagado previo (COMPLETO): ${registro.nombreEvento || 'evento sin nombre'} (tipo: ${registro.tipo}, tipoMotor: ${registro.tipoMotor}) - fecha=${registro.fecha}`);
        }
      }

      if (estadiaInicioAE) {
        const duracionSinCerrar = Math.max(0, Math.floor((convertirFechaLocal(filtros.fecha_fin || registros[registros.length - 1]?.fecha || new Date()) - estadiaInicioAE.fechaInicio) / 1000));
        //console.warn(`[${aliasVehiculo}] 🚫 ESTADÍA A/E SIN CERRAR (COMPLETO) - inicio=${estadiaInicioAE.fechaInicio} ubic=${estadiaInicioAE.ubicacion} duraciónEstim=${duracionSinCerrar}s`);
      }

      //console.log(`[${aliasVehiculo}] 📊 COMPLETO - Total estadías: ${estadias.filter(e => e.uid_vehiculo === vehiculoUid && (e.tipo_reporte === 'Detenidos' || e.tipo_reporte === 'A/E')).length}`);
    }
    /*else if (filtros.tipo_reporte === 2) {
      // ✅ COMPLETO (DETENIDO Y E/A) - EJECUTAR LÓGICAS POR SEPARADO
      //console.log(`[${aliasVehiculo}] Modo: COMPLETO (Detenido + E/A) - Tipo vista: ${filtros.tipo_vista}`);

      // 🔄 EJECUTAR LÓGICA DE DETENIDOS POR SEPARADO
      //console.log(`[${aliasVehiculo}] 📍 Ejecutando lógica de DETENIDOS...`);
      let estadiaInicioDetenido = null;

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE DETENIDO POR TIPOS ESPECÍFICOS
        const esDetenido = registro.tipo === 30 ||
          registro.tipo === 11 ||
          registro.tipo === 12 ||
          registro.tipo === 17 ||
          registro.tipo === 18 ||
          registro.nombreEvento?.toLowerCase().includes('detenido');

        const esMovimiento = registro.nombreEvento?.toLowerCase().includes('movimiento') ||
          registro.tipo === 1 ||
          registro.tipo === 2;

        // INICIO DE ESTADÍA DETENIDO
        if (esDetenido && !estadiaInicioDetenido) {
          const { ubicacion, categoria } = obtenerUbicacion(registro);

          if (cumpleFiltroPorTipoVista(ubicacion, categoria, filtros.tipo_vista)) {
            estadiaInicioDetenido = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria
            };
            //console.log(`[${aliasVehiculo}] 🛑 DETENIDO (COMPLETO): ${ubicacion}`);
          }
        }

        // FIN DE ESTADÍA DETENIDO
        else if (esMovimiento && estadiaInicioDetenido) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicioDetenido.fechaInicio) / 1000);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            const estadia = crearObjetoEstadia({
              vehiculoUid,
              registro: estadiaInicioDetenido.registro,
              fechaInicio: estadiaInicioDetenido.fechaInicio,
              fechaFin: fechaFin,
              ubicacionInicio: estadiaInicioDetenido.ubicacion,
              ubicacionFin: estadiaInicioDetenido.ubicacion,
              categoria: estadiaInicioDetenido.categoria,
              tiempoSegundos,
              tipoReporte: 'Completo (DETENIDO)'
            });

            estadias.push(estadia);
            //console.log(`[${aliasVehiculo}] ✅ ESTADÍA DETENIDO (COMPLETO): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicioDetenido.ubicacion}`);
          }

          estadiaInicioDetenido = null;
        }
      }

      // 🔄 EJECUTAR LÓGICA DE APAGADO/ENCENDIDO POR SEPARADO
      //console.log(`[${aliasVehiculo}] 🔴 Ejecutando lógica de APAGADO/ENCENDIDO...`);
      let estadiaInicioAE = null;

      for (let i = 0; i < registros.length; i++) {
        const registro = registros[i];

        // ✅ DETECTAR EVENTOS DE APAGADO/ENCENDIDO POR TIPOS ESPECÍFICOS
        const esApagado = registro.tipoMotor === 0 ||
          registro.tipo === 5 ||
          registro.tipo === 3 ||
          registro.nombreEvento?.toLowerCase().includes('apagado');

        const esEncendido = registro.tipoMotor === 1 ||
          registro.tipo === 6 ||
          registro.tipo === 4 ||
          registro.nombreEvento?.toLowerCase().includes('encendido');

        // INICIO DE ESTADÍA APAGADO
        if (esApagado && !estadiaInicioAE) {
          const { ubicacion, categoria } = obtenerUbicacion(registro);

          if (cumpleFiltroPorTipoVista(ubicacion, categoria, filtros.tipo_vista)) {
            estadiaInicioAE = {
              registro,
              fechaInicio: convertirFechaLocal(registro.fecha),
              ubicacion,
              categoria
            };
            //console.log(`[${aliasVehiculo}] 🔴 APAGADO (COMPLETO): ${ubicacion}`);
          }
        }

        // FIN DE ESTADÍA APAGADO
        else if (esEncendido && estadiaInicioAE) {
          const fechaFin = convertirFechaLocal(registro.fecha);
          const tiempoSegundos = Math.floor((fechaFin - estadiaInicioAE.fechaInicio) / 1000);

          if (tiempoSegundos >= tiempoMinimoEstadia && tiempoSegundos > 0) {
            const estadia = crearObjetoEstadia({
              vehiculoUid,
              registro: estadiaInicioAE.registro,
              fechaInicio: estadiaInicioAE.fechaInicio,
              fechaFin: fechaFin,
              ubicacionInicio: estadiaInicioAE.ubicacion,
              ubicacionFin: estadiaInicioAE.ubicacion,
              categoria: estadiaInicioAE.categoria,
              tiempoSegundos,
              tipoReporte: 'Completo (APAGADO)'
            });

            estadias.push(estadia);
            //console.log(`[${aliasVehiculo}] ✅ ESTADÍA APAGADO (COMPLETO): ${formatearTiempo(tiempoSegundos)} en ${estadiaInicioAE.ubicacion}`);
          }

          estadiaInicioAE = null;
        }
      }

      //console.log(`[${aliasVehiculo}] 📊 COMPLETO - Total estadías: ${estadias.filter(e => e.uid_vehiculo === vehiculoUid).length}`);

    }*/
    else {
      // ✅ OTROS TIPOS DE REPORTE - MANTENER SIMPLE
      //console.log(`[${aliasVehiculo}] Tipo de reporte ${filtros.tipo_reporte} no implementado todavía`);
    }

    //console.log(`[${aliasVehiculo}] Estadías generadas: ${estadias.filter(e => e.uid_vehiculo === vehiculoUid).length}`);
  }); // ✅ CERRAR forEach de vehículos

  //console.log("[calcularEstadiasMultiples] Total estadías encontradas:", estadias.length);

  // ✅ CALCULAR RESUMEN TOTAL USANDO EL HELPER
  const resumen = calcularResumenTotalEstadias(estadias);

  //console.log("[calcularEstadiasMultiples] Resumen calculado:", {
  //  total_estadias: resumen.total_estadias,
  //  tiempo_total_apagado: formatearTiempo(resumen.tiempo_total_apagado),
  //  tiempo_total_encendido: formatearTiempo(resumen.tiempo_total_encendido),
  //  porcentaje_apagado: resumen.porcentaje_apagado,
  //  porcentaje_encendido: resumen.porcentaje_encendido
  //});

  // ✅ RETORNAR OBJETO CON ESTADÍAS Y RESUMEN
  return {
    estadias: estadias,
    resumen: resumen
  };
};



const formatearFechaLocal = (fecha) => {
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  const hours = String(fecha.getHours()).padStart(2, '0');
  const minutes = String(fecha.getMinutes()).padStart(2, '0');
  const seconds = String(fecha.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * ✅ FUNCIÓN AUXILIAR: Crear objeto de estadía estandarizado
 */
function crearObjetoEstadia({
  vehiculoUid,
  registro,
  fechaInicio,
  fechaFin,
  ubicacionInicio,
  ubicacionFin,
  categoria,
  tiempoSegundos,
  tipoReporte,
  todosLosRegistros = [] // ✅ NUEVO: Array de todos los registros del vehículo
}) {
  const tiempoFormateado = formatearTiempo(tiempoSegundos);

  // ✅ VALIDACIÓN SEGURA DE CATEGORIA
  const categoriaSegura = categoria || 'Indeterminado';
  const tipoSeguro = categoriaSegura.toLowerCase();

  // ✅ CALCULAR TIEMPOS DE ENCENDIDO Y APAGADO USANDO EL HELPER
  let tiempo_encendido = 0;
  let tiempo_apagado = 0;

  try {
    // Usar el helper que analiza intervalos entre registros consecutivos
    const tiemposCalculados = calcularTiemposMotorEstadia(todosLosRegistros, fechaInicio, fechaFin, tipoReporte);
    tiempo_encendido = tiemposCalculados.tiempo_encendido;
    tiempo_apagado = tiemposCalculados.tiempo_apagado;

    // ✅ LOG DETALLADO CUANDO HAY TIEMPO DE MOTOR APAGADO
    if (tiempo_apagado > 0) {
      //console.log(`[crearObjetoEstadia] 🔴 TIEMPO MOTOR APAGADO CALCULADO: ${formatearTiempo(tiempo_apagado)} (Encendido: ${formatearTiempo(tiempo_encendido)}) - Estadía de ${fechaInicio} a ${fechaFin}`);
    }

    // console.log(`[crearObjetoEstadia] 🔧 Tiempos calculados con helper:`, {
    //   vehiculo: vehiculoUid,
    //   tiempo_encendido: formatearTiempo(tiempo_encendido),
    //   tiempo_apagado: formatearTiempo(tiempo_apagado),
    //   total_registros: todosLosRegistros.length
    // });
  } catch (error) {
    //console.warn(`[crearObjetoEstadia] ⚠️ Error calculando tiempos de motor:`, error);

    // ✅ FALLBACK: Usar lógica anterior como respaldo
    //console.log(`[crearObjetoEstadia] 🔄 USANDO FALLBACK para calcular tiempos de motor - TipoReporte: ${tipoReporte}, TipoMotor: ${registro.tipoMotor}`);

    if (tipoReporte === 'A/E' || tipoReporte === 'Completo (APAGADO)' || tipoReporte === 'Completo (DETENIDO)') {
      if (registro.tipoMotor === 0) {
        tiempo_apagado = tiempoSegundos;
        tiempo_encendido = 0;
        //console.log(`  🔴 FALLBACK MOTOR APAGADO: ${formatearTiempo(tiempo_apagado)} - Registro tipo: ${registro.tipo}, evento: "${registro.nombreEvento}", fecha: ${registro.fecha}`);
      } else if (registro.tipoMotor === 1) {
        tiempo_encendido = tiempoSegundos;
        tiempo_apagado = 0;
        //console.log(`  🟢 FALLBACK MOTOR ENCENDIDO: ${formatearTiempo(tiempo_encendido)} - Registro tipo: ${registro.tipo}, evento: "${registro.nombreEvento}", fecha: ${registro.fecha}`);
      }
    } else {
      if (registro.tipoMotor === 0) {
        tiempo_apagado = tiempoSegundos;
        //console.log(`  🔴 FALLBACK MOTOR APAGADO (DETENIDOS): ${formatearTiempo(tiempo_apagado)} - Registro tipo: ${registro.tipo}, evento: "${registro.nombreEvento}", fecha: ${registro.fecha}`);
      } else if (registro.tipoMotor === 1) {
        tiempo_encendido = tiempoSegundos;
        //console.log(`  🟢 FALLBACK MOTOR ENCENDIDO (DETENIDOS): ${formatearTiempo(tiempo_encendido)} - Registro tipo: ${registro.tipo}, evento: "${registro.nombreEvento}", fecha: ${registro.fecha}`);
      }
    }
  }

  //console.log(`[crearObjetoEstadia] 🏗️ Creando estadía:`, {
  //   vehiculoUid,
  //   ubicacionInicio,
  //   ubicacionFin,
  //   categoria: categoriaSegura,
  //   tipo: tipoSeguro,
  //   tiempoFormateado,
  //   tipoReporte,
  //   tiempo_encendido: formatearTiempo(tiempo_encendido),
  //   tiempo_apagado: formatearTiempo(tiempo_apagado),
  //   direccion: registro.direccion || registro.Direccion || 'N/A'
  // });

  //console.log('estadia creacion final');
  //console.log('fechaInicio:', fechaInicio, 'fechaFin:', fechaFin, 'tiempoSegundos:', tiempoSegundos);

  return {
    uid_vehiculo: vehiculoUid,
    no_economico: registro.vehiculos?.noEconomico || 'N/A',
    alias: registro.vehiculos?.alias || 'N/A',
    chofer: registro.chofer?.nombreChofer || '',
    fecha_inicio: formatearFechaLocal(fechaInicio), //convertirFechaLocal(fechaInicio),  //fechaInicio.toISOString().slice(0, 19).replace('T', ' '),
    fecha_fin: formatearFechaLocal(fechaFin),//.toISOString().slice(0, 19).replace('T', ' '),
    punto_inicio: ubicacionInicio,
    punto_fin: ubicacionFin || ubicacionInicio,
    tiempo: tiempoFormateado,
    tiempo_segundos: tiempoSegundos,
    tiempo_encendido: tiempo_encendido,  // ✅ NUEVO CAMPO
    tiempo_apagado: tiempo_apagado,      // ✅ NUEVO CAMPO
    tipo_reporte: tipoReporte,
    tipo: tipoSeguro,
    latitud: registro.latitud || 0,
    longitud: registro.longitud || 0,
    evento_inicio: registro.nombreEvento || '',
    direccion: registro.direccion || registro.Direccion || '',
    categoria: categoriaSegura,
    alerta_real: registro.nombreEvento || '',
    tipo_real: registro.tipo,
    tipo_motor_inicio: registro.tipoMotor  // ✅ INFORMACIÓN ADICIONAL
  };
}

// function crearObjetoEstadia({
//   vehiculoUid,
//   registro,
//   fechaInicio,
//   fechaFin,
//   ubicacionInicio,
//   ubicacionFin,
//   categoria,
//   tiempoSegundos,
//   tipoReporte
// }) {
//   const tiempoFormateado = formatearTiempo(tiempoSegundos);

//   return {
//     uid_vehiculo: vehiculoUid,
//     no_economico: registro.vehiculos?.noEconomico || 'N/A',
//     alias: registro.vehiculos?.alias || 'N/A',
//     chofer: registro.chofer?.nombreChofer || '',
//     fecha_inicio: fechaInicio.toISOString().slice(0, 19).replace('T', ' '),
//     fecha_fin: fechaFin.toISOString().slice(0, 19).replace('T', ' '),
//     punto_inicio: ubicacionInicio,
//     punto_fin: ubicacionFin || ubicacionInicio,
//     tiempo: tiempoFormateado,
//     tiempo_segundos: tiempoSegundos,
//     tipo_reporte: tipoReporte,
//     tipo: categoria.toLowerCase(),
//     latitud: registro.latitud || 0,
//     longitud: registro.longitud || 0,
//     evento_inicio: registro.nombreEvento || '',
//     categoria: categoria
//   };
// }

// ✅ EXPORTAR CONSTANTES
export { Constantes };